import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  formatPercentage,
  RIDE_STATUSES,
  suggestDressagePlacings,
  compareDressagePlacingCandidates,
  placingCandidateFromResultRow,
  isJudgeEnteredPlacingClassType,
  TIE_BREAK_DISPLAY_FIELDS,
  classifyTestForPlacing,
  classifyDshbClassKind,
  DSHB_CLASS_KINDS,
  tieBreakDisplayFieldForClassType,
  DRESSAGE_PLACING_CLASS_TYPES,
  resolveDisplayTermReason,
  suppressScoreValue,
  filterVisibleClasses,
  panelSizeBoundsViolation,
  expectedJudgeCountForClass,
  placedByDivisionKey,
  explicitPlacesAwarded,
  placingGroupKeyOf,
  placingGroupsOf,
  lockedPlacingGroupKeys,
} from "score-common";
import { cleanRiderStatus } from "score-common/rider-status";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow, RideStatusBadge, useActiveOrg } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useRideStatuses } from "../hooks/useRideStatuses";
import { useShowChangeCheck, useOnTick } from "../hooks/useShowChangeCheck";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import { useLoadedFor } from "../hooks/useLoadedFor";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddFilterMenu from "../components/AddFilterMenu";
import RideLink from "../components/RideLink";
import FinalClassLockNotice, { PLACE_BUTTON_ANCHOR_ID } from "../components/FinalClassLockNotice";
import { useBadgeCounts } from "../context/BadgeCountsContext";
import FvdPushStatusIcon from "../components/FvdPushStatusIcon";
import NoScoreRecordedDialog from "../components/NoScoreRecordedDialog";
import PlaceByRulesDialog from "../components/PlaceByRulesDialog";
import { authoritativeSheetsOnly } from "../util/contenderCompare";
import {
  classNumberLabel,
  classDayKey,
  compareByClassNumber,
  formatClassStartTime,
  todayDayKey,
  getRideTestName,
  getCatalogTestById,
} from "../util/classDisplay";
import {
  classNeedsPlacings,
  divisionsNeedingPlacings,
  outOfDateMoves,
  outOfDateOptions,
} from "../util/classNeedsPlacings";
import { placingNeedsHumanReason, NEEDS_HUMAN_REASONS } from "../util/placingNeedsHuman";
import { RELATED_CLASS_REPORTS, relatedReportPath } from "../util/relatedReports";
import { hasOverridePlacedClassPermission } from "../permissions";

// #559 / #3559 -- the live status badges and the placings re-read when the show's
// change check says the SELECTED CLASS moved (its counter, c#<classId>), plus the
// 60 s backstop (hooks/useShowChangeCheck.js). They used to re-poll every 10 s
// whether or not anything had changed.

// #839 — batch-entry placing editor + single "Place" button (replaces the old
// per-row auto-save-on-blur flow — bugs #50/#60/#66: random order, no save,
// no confirmation). Shows the multi-judge result for every ride in a class
// (getClassResults, already sorted placing-asc then composite-desc — see
// score-api handlers/results.js). The operator enters ALL placings for the
// class into local edit buffers; NOTHING is written to score-api until Place
// is pressed, which commits every row in one atomic request
// (ResultsApi.setClassPlacings, a DynamoDB TransactWriteCommand server-side)
// and — only when the whole class is releasable (every non-scratched ride
// CONFIRMED/FINAL, mirroring foxbox's isReleased) — makes those placings
// available to foxbox's next FVD pull. If the class isn't fully releasable
// yet, the server blocks the ENTIRE commit (409, nothing written) and names
// the rides still holding it up.
//
// getClassResults doesn't carry a rider/horse label (only rideId/classId/
// perJudge/composite/collectiveTotal/placing/status/completedCount/
// expectedJudgeCount — collectiveTotal added by #946, same withhold-until-
// every-judge-confirmed policy as composite), so rows are labeled by joining
// against rides.listRides(classId) — the same join RideEditor's RidePicker
// does for its ride select.

// #822 hook — scoresheet-gen + competitor-email automation attaches HERE.
// This card (#839) guarantees this is called exactly once, synchronously
// after a SUCCESSFUL (releasable) Place commit, with the classId and the
// server's { classId, placed, releasable } response — and that a failure in
// here never blocks or hides the success confirmation already shown to the
// operator. #822 replaces this no-op with the real fire-and-forget call.
async function triggerScoresheetGenAndEmail(/* classId, placeResponse */) {
  // no-op — implemented by #822.
}


// #811 — a ride that can never earn a competitive placing (scratched before
// it ran, or eliminated/withdrawn/retired mid-test) doesn't belong mixed in
// with placeable rides, regardless of the rider's division. SCRATCHED is the
// raw stored Ride status (ridesById mirrors listRides, so this reads the same
// field ClassManager/RideEditor already gate on); "eliminated" has no status
// of its own — it's any confirmed sheet carrying a non-empty eliminations
// list, exactly how score-api's buildRideResult flags entry.eliminated on
// perJudge (mirrors calculation.js's `eliminated` — WD/retired rides are
// logged as an elimination reason, so this one check also covers "terminated").
//
// #945 — this is deliberately the ONLY "term'd" check (AC3: scratch/no-show/
// eliminated/withdrawn all sort to the bottom). There is no broader status
// enum to reach for: FVD has no RET/WD/no-show taxonomy of its own — foxbox's
// mapRideStatus (apps/foxbox/src/read/mapper.js) collapses EVERY non-empty
// chrTermReason (scratch, no-show, withdraw, retire, ...) into the single
// stored status "SCRATCHED" before it ever reaches score-api, keeping the raw
// reason only as ride.termReason (display-only). score-common's
// DERIVED_RIDE_STATUSES (utils/ride-status.js) mirrors the same SCRATCHED
// value and adds no separate WITHDRAWN/NO_SHOW member. A mid-test withdrawal
// is likewise not a status — RideEditor's ELIMINATION_REASONS includes
// "Withdrawn / retired" as an elimination reason, landing in perJudge[].
// eliminated exactly like any other elimination. So SCRATCHED + eliminated
// together are already the complete terminal set.
function isBottomTierRow(row, ridesById) {
  if (ridesById[row.rideId]?.status === RIDE_STATUSES.SCRATCHED) return true;
  return !!row.perJudge?.some((j) => j.eliminated);
}

// #529 — the explicit ordering used on every load/refetch (initial select AND
// post-Place refresh): placing ascending (unplaced rides last), then ride
// number ascending as a stable, predictable tie-break. #811 — scratched and
// eliminated/terminated rides are pulled into a forced-last tier ahead of
// that comparison, so they always sink to the bottom regardless of placing.
function sortResults(results, ridesById) {
  return [...results].sort((a, b) => {
    const aBottom = isBottomTierRow(a, ridesById);
    const bBottom = isBottomTierRow(b, ridesById);
    if (aBottom !== bBottom) return aBottom ? 1 : -1;

    const ap = a.placing;
    const bp = b.placing;
    if (ap != null && bp != null) {
      if (ap !== bp) return ap - bp;
    } else if (ap != null) {
      return -1;
    } else if (bp != null) {
      return 1;
    }
    const an = ridesById[a.rideId]?.rideNumber ?? Infinity;
    const bn = ridesById[b.rideId]?.rideNumber ?? Infinity;
    return an - bn;
  });
}

const TERMD_GROUP_KEY = "__termd__";

// #3261 AC5 — static reason-code -> i18n-key map for the "needs a human"
// notice (placingNeedsHumanReason's codes). Literal keys only, never built
// from the code string.
const NEEDS_HUMAN_TEXT_KEYS = {
  [NEEDS_HUMAN_REASONS.UNBROKEN_TIE]: "placings.needsHuman.unbrokenTie",
  [NEEDS_HUMAN_REASONS.JUDGE_PLACE_MISSING]: "placings.needsHuman.judgePlaceMissing",
  [NEEDS_HUMAN_REASONS.JUDGE_PLACE_SHARED]: "placings.needsHuman.judgePlaceShared",
  [NEEDS_HUMAN_REASONS.RIDE_OFF_OWED]: "placings.needsHuman.rideOffOwed",
};
// #3405 -- a ride with no rider status counts as Open (workstream
// placed-by-division). It shares the key of an explicit "Open" status, so the
// two are ONE group, and there is no separate "no division" bucket. The key,
// the Open fold and the "Yes"/"No" fold-into-Open rule all come from
// score-common (placedByDivisionKey), the SAME functions
// the backend auto-placer groups by, so the page can never disagree with it.

// #1420 — map a getClassResults display row into the shape score-common's
// comparator reads, so the placing views rank through the SAME single USEF
// comparator as the suggested view and score-api (AC1). The row's `composite`
// is the comparator's `percentage`; its flat `artisticTotal` (#1999) becomes
// the FREESTYLE chain's `freestyle.artistic` operand and `movementTotal`
// (#2260) the DSHB chain's operand, so DR129.5 (artistic) and DR211.4
// (movement totals) break those classes' ties on the rule-correct number
// instead of Collective Marks. youngHorse.*/feiChildren.* sub-marks are not
// carried on the row, so those chains' steps go unusable and an equal
// percentage stays tied — rule-correct (can't break a tie on absent data),
// never a fabricated order.
const rowToPlacingCandidate = placingCandidateFromResultRow;

// #945/#946/#1420 — the comparator every placing view sorts with (highest
// first; a row with no composite yet sorts last). #1420 collapsed the
// FE-local percentage→collectiveTotal chain this used to reimplement onto
// score-common's compareDressagePlacingCandidates, so there is ONE operative
// comparator (AC1) shared with the suggested view, score-api's
// checkPlacingImpactOnCommit, and report-defs' high-point. `classType` (a
// DRESSAGE_PLACING_CLASS_TYPES value, resolved per class in
// buildPlacingGroups) selects the rule-correct chain for that class.
//
// The FE keeps ONLY two display-level nulls-last guards, which are placing-
// screen concerns (PARTIAL/legacy data), NOT USEF tie-breaks:
//   - a row with no composite yet (#697 withheld PARTIAL, or a place-only
//     class not yet scored) sorts below any scored row;
//   - a scored row the rule chain can't separate but that carries a known
//     collectiveTotal sorts above one with none (a PARTIAL ride, or a sheet
//     confirmed before #946) — #946's nulls-last. A true tie (both known and
//     equal, or both missing) falls through to 0, which with Array.sort's
//     spec-guaranteed stability leaves the rows in API order: an unbreakable
//     tie remains tied and is never reordered or auto-resolved (AC3).
export function compareByComposite(a, b, classType) {
  const ac = a?.composite;
  const bc = b?.composite;
  if (ac == null && bc == null) return 0;
  if (ac == null) return 1;
  if (bc == null) return -1;

  const { cmp } = compareDressagePlacingCandidates(
    rowToPlacingCandidate(a),
    rowToPlacingCandidate(b),
    { classType },
  );
  if (cmp !== 0) return cmp;

  const at = a?.collectiveTotal;
  const bt = b?.collectiveTotal;
  if (at == null && bt == null) return 0;
  if (at == null) return 1;
  if (bt == null) return -1;
  return 0;
}

// #3261 -- a judge-entered class (Equitation / Medal / DSHB placement-only)
// is ordered by the place the JUDGE wrote (judgeEnteredPlace, a lower number
// first, no place last) before anything else: its rows may carry no
// percentage at all, so composite order alone leaves them in API order. This
// is what the removed USEF-suggested view used to show for these classes.
// Every other class type, and rows the judge's place does not separate, sort
// by compareByComposite exactly as before.
function judgePlaceRank(row) {
  const place = row?.judgeEnteredPlace;
  return Number.isInteger(place) && place > 0 ? place : Infinity;
}

function sortByComposite(rows, classType) {
  const judgeEntered = isJudgeEnteredPlacingClassType(classType);
  return [...rows].sort((x, y) => {
    if (judgeEntered) {
      const byPlace = judgePlaceRank(x) - judgePlaceRank(y);
      if (byPlace !== 0 && !Number.isNaN(byPlace)) return byPlace;
    }
    return compareByComposite(x, y, classType);
  });
}

// #1420 / AC3 — is a set of rides that all share ONE placing value a
// LEGITIMATE shared place (an unbreakable tie) rather than an entry error?
// Exactly when every ride is scored (carries a composite) and the single USEF
// comparator cannot separate ANY of them from the first (DR123.3: "the horses
// must remain tied" — a shared placing is the rule-mandated representation of
// that tie, not a collision). Uses compareDressagePlacingCandidates directly
// (NOT compareByComposite) so the decision is the pure rule chain: a missing
// collectiveTotal on one side is "can't break the tie" (tied), not the
// display-only nulls-last ordering compareByComposite adds for the table. An
// unscored ride (no composite — PARTIAL, or a place-only class not yet judged)
// can never confirm a score tie, so a group containing one is NOT a
// legitimate tie and still warns. Any group the comparator DOES rank (e.g.
// 68% vs 71% both typed as "1") is a real collision and still warns. A
// judge-entered class (Dressage Seat Equitation / Medal, DSHB placement-only:
// DR133.1.k / DR133.2.g.4 / DR210.5) is never confirmable here either: its
// place is the JUDGE's number (and a Medal tie owes a ride-off, DR133.2.g.5),
// not something this comparator can certify, so a shared number still warns.
function isLegitimateTieGroup(rows, classType) {
  if (!Array.isArray(rows) || rows.length < 2) return false;
  if (isJudgeEnteredPlacingClassType(classType)) return false;
  if (rows.some((r) => r?.composite == null)) return false;
  const cands = rows.map(rowToPlacingCandidate);
  const first = cands[0];
  return cands.every(
    (c, i) => i === 0 || compareDressagePlacingCandidates(first, c, { classType }).cmp === 0,
  );
}

// #946 — +/-/= indicator (AC3) for a group of rows that share the same
// rendered "competitive pool" (one buildPlacingGroups group — the same unit
// sortByComposite already sorts within). Groups rows by composite value
// (not adjacency — the "default" view's rows aren't composite-sorted, so a
// tie needs to be found by value everywhere the indicator is shown), then
// within each 2+ group ranks the KNOWN collectiveTotals:
//   - a row whose collectiveTotal is missing (null/undefined — a PARTIAL
//     ride, or a sheet confirmed before #946 shipped) is UNKNOWN, not a
//     loss: it gets no indicator at all, never "-" (never returned in the
//     map).
//   - among rows with a known collectiveTotal, the top value wins: if
//     exactly ONE row holds it, that row gets "+" (won the tie-break
//     outright). If TWO OR MORE rows share the top value, they're tied WITH
//     EACH OTHER, not each a sole winner — they all get "=" instead (this is
//     also what makes an across-the-board equal group, AC2's true tie, come
//     out "=" for everyone: the whole known set IS the top tier). Any known
//     value below the top gets "-".
//   - fewer than 2 rows with a KNOWN collectiveTotal in the group means
//     there's nothing to compare (can't tell if the lone known value beat an
//     unknown one) — no indicators at all for that group.
// Returns a rideId -> "+"|"-"|"=" Map.
function tieIndicatorsFor(rows) {
  const byComposite = new Map();
  for (const row of rows) {
    if (row?.composite == null) continue;
    const group = byComposite.get(row.composite);
    if (group) group.push(row);
    else byComposite.set(row.composite, [row]);
  }

  const indicators = new Map();
  for (const group of byComposite.values()) {
    if (group.length < 2) continue;
    const known = group.filter((r) => r.collectiveTotal != null);
    if (known.length < 2) continue;

    const max = Math.max(...known.map((r) => r.collectiveTotal));
    const atMax = known.filter((r) => r.collectiveTotal === max).length;
    for (const row of known) {
      if (row.collectiveTotal !== max) indicators.set(row.rideId, "-");
      else indicators.set(row.rideId, atMax > 1 ? "=" : "+");
    }
  }
  return indicators;
}

// #946 — the visible +/-/= glyph next to a tied row's composite. Every
// string comes from a static t() call (no dynamic key construction) so the
// FE-side i18n map stays literal-scannable. `value` is undefined for a
// non-tied row, in which case nothing renders.
function TieIndicator({ value, rideId, t }) {
  if (!value) return null;
  const meta =
    value === "+"
      ? { symbol: "+", label: t("placings.tieIndicator.higherLabel"), color: "success.main" }
      : value === "-"
        ? { symbol: "−", label: t("placings.tieIndicator.lowerLabel"), color: "error.main" }
        : { symbol: "=", label: t("placings.tieIndicator.equalLabel"), color: "text.secondary" };
  return (
    <Typography
      component="span"
      data-testid={`placing-tie-indicator-${rideId}`}
      aria-label={meta.label}
      title={meta.label}
      sx={{ ml: 0.75, fontWeight: 700, color: meta.color }}
    >
      {meta.symbol}
    </Typography>
  );
}

// #1288 — resolve the class's DRESSAGE_PLACING_CLASS_TYPES the same way
// score-api's checkPlacingImpactOnCommit does (results.js): the first ride
// whose testId resolves to a real catalog Test speaks for the whole class
// (same homogeneity assumption, same STANDARD default when nothing
// resolves — see that function's own comment for the caveat on mixed
// classes). Unlike the server, which fetches each Test from DynamoDB, this
// resolves entirely client-side off the already-loaded `ridesById`: every
// Ride carries its own testId (#915), and getCatalogTestById is a
// synchronous lookup against catalogDefsById (#1665: the LIVE score-catalog
// table, hydrated into IndexedDB by useCatalogDefs — see classDisplay.js),
// so no extra network round trip is needed here.
//
// #2209 — checked FIRST, off the selected CLASS's own className via
// classifyDshbClassKind (score-common), the same signal score-api's
// effectiveTestForCalculation (scoresheet.js) uses server-side to pick the
// "placement-only" scoringModel override. Independent of the per-row test
// loop below: FVD stamps a championship/suitability class with its
// QUALIFYING class's Test id (classifyDshbClassKind's own doc comment), so
// the real catalog Test this screen resolves per ride never carries
// anything that would let classifyTestForPlacing recognize a DSHB class on
// its own — the class's real name is the only reliable signal available
// client-side too. DR211.5 (docs/rules/usef/rulebook-dr-dressage-2026.pdf,
// © USEF 2026): "all scores (if given) and placings must be published" —
// a place-only class's rides have to reach this suggestion the same as any
// other class type.
//
// #2209 (judge review, FIX 1) — mirrors score-api's resolveClassTypeForPlacing
// (results.js): the className match ALONE is not the gate.
// classifyDshbClassKind's CHAMPIONSHIP_NAME_PATTERN is a bare className
// regex with no DSHB gate of its own (#2202's job, not this card's), so it
// fires just as readily on an ordinary percentage-scored
// "USDF Regional Championship Fourth Level Open" class. Routing that class
// to DSHB_PLACEMENT_ONLY would blank its Place-screen suggestion (empty
// buildChain by design) and its tie-break column
// (tieBreakDisplayFieldForClassType), even though it has real percentages
// to rank on. `rows` here already carries each ride's computed `composite`
// (getClassResults' shape, the same field suggestedPlacingsFor filters on
// below) — checked across every row so an all-still-PARTIAL DSHB class
// (composite genuinely null for every row so far) is not mistaken for a
// class with no percentage at all having its rows filtered out first.
function resolveClassTypeForSuggestion(rows, ridesById, catalogDefsById, classItem) {
  // #2277 — override wins over the className heuristic.
  const dshbKind = classifyDshbClassKind({
    className: classItem?.className,
    dshbKindOverride: classItem?.dshbClassKindOverride,
  });
  const dshbNamedPlacementOnly =
    dshbKind === DSHB_CLASS_KINDS.CHAMPIONSHIP || dshbKind === DSHB_CLASS_KINDS.SUITABILITY;
  const rowsCarryNoPercentage = !(rows ?? []).some(
    (r) => typeof r?.composite === "number" && Number.isFinite(r.composite)
  );
  if (dshbNamedPlacementOnly && rowsCarryNoPercentage) {
    return DRESSAGE_PLACING_CLASS_TYPES.DSHB_PLACEMENT_ONLY;
  }
  for (const row of rows) {
    const test = getCatalogTestById(ridesById[row.rideId]?.testId, catalogDefsById);
    if (test) return classifyTestForPlacing(test);
  }
  return DRESSAGE_PLACING_CLASS_TYPES.STANDARD;
}

// #1999: i18n column-header key for the active tie-break display field
// (see tieBreakField above). Static per-field lookup, not built from the
// field string, so every case has a literal t() key.
const TIE_BREAK_COLUMN_LABEL_KEYS = {
  [TIE_BREAK_DISPLAY_FIELDS.COLLECTIVE_MARKS]: "placings.columns.collectiveMarks",
  [TIE_BREAK_DISPLAY_FIELDS.ARTISTIC_MARKS]: "placings.columns.artisticMarks",
  // #2206 — DR211.4's own display number (DSHB only, see
  // tieBreakDisplayFieldForClassType above); this map had no key for it,
  // so the column rendered with t(undefined) as its header: a blank-headed
  // column of numbers in front of a judge deciding placings.
  [TIE_BREAK_DISPLAY_FIELDS.MOVEMENT_TOTALS]: "placings.columns.movementTotal",
};

// #1999/#2077: renders row[tieBreakField] (a raw points sum, DR123.3/
// DR129.5's own basis, not a percentage, so formatPercentage does not
// apply). null means "not known yet" (a PARTIAL ride, or old data), shown
// as a dash rather than 0 (same "unknown, not zero" contract as
// collectiveTotal itself, see sumCollectiveTotal/sumArtisticTotal in
// score-api).
//
// #2077: ONE decimal, not three. This is a sum of individually-awarded
// collective/artistic marks, each a coefficient times a mark scored in
// DR122.8's default half-point increments (0.5) or one of DR122.9's
// enumerated 0.1 grants (Freestyle artistic, Young Horse, 7-year-old/
// Children quality-only) (`docs/rules/usef/rulebook-dr-dressage-2026.pdf`,
// "© USEF 2026", current edition per docs/rules/README.md). Neither DR122.8
// nor DR122.9 constrains the coefficient itself to an integer — one decimal
// is lossless here only because every catalog def's actual coefficients are
// 1/2 (collectives) or 2/4 (artistic movements), never a fraction that
// would need a second digit; a future catalog def with a fractional
// coefficient would need this re-checked, not assumed. DR123.3's Young
// Horse tie-break ("combined and divided by two") is a rule-sanctioned
// 0.05-granularity path, but this column never renders it: the candidates
// built above carry no youngHorse.* sub-marks (see the block comment
// higher in this file), so today's actual render inputs are never finer
// than a tenth — that is a fact about what reaches this function today, not
// a guarantee the rule itself makes. DR123.3 itself calls the ordinary
// (non-Young-Horse) case a "total marks" comparison, not a percentage —
// matching the app's own existing convention for a mark-based total:
// report-defs' `mark()` helper (packages/report-defs/src/helpers.js)
// already renders every dressage mark at one decimal, and score-common's
// `formatPoints` renders a standard (non-freestyle) raw-score points total
// at one decimal too. Only `formatPercentage` (the Composite column, AC2)
// stays at three decimals — that's a real percentage, unaffected by this
// change.
function formatTieBreakValue(value) {
  return typeof value === "number" ? value.toFixed(1) : "-";
}

// #953 / #1288 — maps placeable rows into USEF placing-suggestion candidates
// and returns a rideId -> suggestion Map via score-common's
// suggestDressagePlacings (packages/score-common/src/utils/placing.js; full
// rule citations in docs/score/usef-dressage-placing-rules.md), using the
// class's real classType (resolveClassTypeForSuggestion above) instead of
// always forcing STANDARD.
//
// The candidates built below still only ever carry { percentage,
// collectiveTotal, movementTotal } — no freestyle.*/feiChildren.*/
// youngHorse.* sub-marks
// (submission/perspective, technical, artistic/harmony/choreography), which
// getClassResults' per-row shape (perJudge/composite/collectiveTotal/
// placing/status/completedCount/expectedJudgeCount) doesn't carry today —
// so this does NOT implement DR129.5's real FREESTYLE_ARTISTIC/HARMONY/
// CHOREOGRAPHY or FEI_CHILDREN_TECHNICAL/MEDIAN tie-break steps. What
// resolving the real classType DOES fix: FREESTYLE and FEI_CHILDREN's own
// chains have no COLLECTIVE_MARKS step at all, so an exact percentage tie in
// one of those classes now correctly renders as tied (DR129.5: collective
// marks must not break it) instead of being silently split by
// collectiveTotal, a comparison those rules don't call for. YOUNG_HORSE
// keeps COLLECTIVE_MARKS as its own step 2 same as STANDARD, so it's
// unaffected either way with today's candidate shape. A row whose composite
// is still withheld (PARTIAL, #697) can't be ranked at all and is simply
// absent from the returned Map — advisory UI only, so "no suggestion yet"
// beats a fabricated one.
function suggestedPlacingsFor(rows, ridesById, catalogDefsById, classItem, classTypeOverride) {
  const classType =
    classTypeOverride ?? resolveClassTypeForSuggestion(rows, ridesById, catalogDefsById, classItem);
  const isDshbPlacementOnly = classType === DRESSAGE_PLACING_CLASS_TYPES.DSHB_PLACEMENT_ONLY;
  // #2209 — a DSHB_PLACEMENT_ONLY ride's composite is null by design
  // (score-common's calculation.js placement-only branch omits
  // percentage/totalPoints entirely, #2183): dropping it here on
  // `composite != null` is exactly the bug this card fixes, and left the
  // Place screen suggesting nothing at all for a place-only class. DR211.5
  // (docs/rules/usef/rulebook-dr-dressage-2026.pdf, © USEF 2026): "all
  // scores (if given) and placings must be published" — the "(if given)"
  // is exactly this case, so the composite gate must not apply to this
  // classType.
  //
  // #2209 (judge review) — bypassing `composite != null` also bypasses
  // #697's own withholding contract, whose doc comment above (the block
  // just above this function) says a PARTIAL row "is simply absent from
  // the returned Map". Without a status check a half-judged DSHB
  // Championship/Suitability class would put every not-yet-judged row
  // (judgeEnteredPlace: null) into one large tie group at the bottom
  // instead of withholding them the way every other classType already
  // does — presentational only (no score leaks either way), but an
  // undeclared change to a stated contract, so the same withhold-until-
  // PARTIAL-is-over rule applies here too.
  const candidates = rows
    .filter((r) => r.composite != null || (isDshbPlacementOnly && r.status !== "PARTIAL"))
    .map((r) => ({
      id: r.rideId,
      percentage: r.composite,
      collectiveTotal: r.collectiveTotal,
      // #2206 — DR211.4's own operand (buildChain's DSHB case,
      // score-common/utils/placing.js: { code: MOVEMENT_TOTALS, get: (c) =>
      // c.movementTotal }). Without this the DSHB chain's tier-2 step has
      // no data to compare even once getClassResults' row carries a real
      // movementTotal (#2260), and a percentage tie in a DSHB class would
      // silently fall through to UNRESOLVED ("must remain tied") instead of
      // ever separating on movement-score totals.
      movementTotal: r.movementTotal ?? null,
      // #1752 — the judge's own Place-column entry, carried on the
      // getClassResults row. For an EQUITATION/EQUITATION_MEDAL class
      // (USEF DR133) or a DSHB_PLACEMENT_ONLY class (USEF DR211.5, #2209)
      // this IS the suggestion: the judge writes the place directly and
      // this screen shows it back for the operator to commit, rather than
      // ranking percentages. Ignored for every other classType.
      judgeEnteredPlace: r.judgeEnteredPlace ?? null,
      // #3365: hors de concours (USEF DR119.5) takes no place; the engine
      // leaves it out, so it has no suggestion.
      hc: r.hc === true,
    }));
  return new Map(suggestDressagePlacings(candidates, { classType }).map((s) => [s.id, s]));
}

// #945 — riderStatus is FVD free text (tblRider.chrStatus), not a closed
// enum, so "Open" / "open " / "OPEN" are the same division typed three
// different ways in FVD and must not split into three separator groups.
// Group by a trimmed+case-folded key; the DISPLAYED label keeps the
// first-seen original (trimmed) casing rather than inventing one. A ride with
// no rider status is Open (#3405), so it lands in the "open" key. #2049 —
// extracted so the #904 duplicate-placing guard in the division view scopes
// by the SAME normalized key the displayed grouping uses, so the guard can
// never disagree with what the operator sees on screen.
function divisionKeyForRow(row, ridesById) {
  return placedByDivisionKey(ridesById[row.rideId]?.riderStatus);
}

// #945/#3091 — buckets rows by divisionKeyForRow into an insertion-ordered
// Map of normalizedKey -> { label, rows }. The label is the first-seen
// trimmed original casing, or "Open" for rows that carry no status. Shared by the "division" view and the "suggested" view (#3091) so
// both group by exactly the same key.
function groupRowsByDivision(rows, ridesById, t) {
  const byDivision = new Map();
  for (const row of rows) {
    const key = divisionKeyForRow(row, ridesById);
    const label =
      cleanRiderStatus(ridesById[row.rideId]?.riderStatus) ?? t("placings.view.open");
    const existing = byDivision.get(key);
    if (existing) {
      existing.rows.push(row);
    } else {
      byDivision.set(key, { label, rows: [row] });
    }
  }
  return byDivision;
}

// #3091 — the division keys of a division map in display order, alphabetical.
function orderedDivisionKeys(byDivision) {
  return [...byDivision.keys()].sort((a, b) => a.localeCompare(b));
}

// #3091/#3119 — USEF suggested order, ranked WITHIN each division — but
// ONLY where the class is set to be placed by division. DR126.2.a.3
// (docs/rules/usef/rulebook-dr-dressage-2026.pdf, © USEF 2026): "Divisions or
// sections of a class are considered to be a separate 'class' only if listed
// as such in the prize list." DR126.2.a.5: "When entries warrant competitions
// are encouraged to split classes into separate divisions..." — encouraged,
// never mandated. FVD cannot say which, so Class.placedByDivision (#3402, set
// in Score Admin) IS that listing, and grouping happens only when it is true.
// A class not placed by division is ONE standing even if riders carry
// different riderStatus values: class-wide suggestion, no headed groups. When it does split, two riders in
// different divisions never compete, so each division gets its own 1st,
// 2nd, ... (same scoping the division view's duplicate-placing guard already
// uses, #2049). The class type is resolved ONCE over the whole class and
// handed to every division so a division's subset of rows can't flip it
// (e.g. DSHB's "no row carries a percentage" test). With fewer than two
// distinct rider-status buckets among the rows there is likewise nothing to
// split and this is exactly the class-wide suggestion.
// Returns { suggestions: rideId -> suggestion Map, byDivision: the division
// map when the class lists 2+ statuses AND 2+ buckets exist, else null }.
function suggestedPlacingsByDivision(placeable, ridesById, catalogDefsById, classItem, t, classTypeOverride) {
  const classType =
    classTypeOverride ??
    resolveClassTypeForSuggestion(placeable, ridesById, catalogDefsById, classItem);
  const byDivision = groupRowsByDivision(placeable, ridesById, t);
  if (classItem?.placedByDivision !== true || byDivision.size < 2) {
    return {
      suggestions: suggestedPlacingsFor(placeable, ridesById, catalogDefsById, classItem, classType),
      byDivision: null,
    };
  }
  const suggestions = new Map();
  for (const { rows } of byDivision.values()) {
    for (const [id, s] of suggestedPlacingsFor(rows, ridesById, catalogDefsById, classItem, classType)) {
      suggestions.set(id, s);
    }
  }
  return { suggestions, byDivision };
}

// #3261 — builds the ordered { key, label, rows }[] groups the table renders
// (one gray separator row per group with a non-null label). Whether the page
// groups by division is NOT the operator's choice any more (the View
// dropdown is gone): `groupByDivision` is the class's own Placed by division
// setting (#3402/#3405, Class.placedByDivision), the SAME field the backend
// auto-placer obeys (#3403), so the page and the auto-placer cannot disagree.
// It is NOT a count of the divisions among the rides: a class not placed by
// division stays ONE standing even with a mix of rider statuses.
//
// Term'd rides (scratched/eliminated/WD/retired, #811's isBottomTierRow)
// never earn a competitive placing, so they always sit in one forced-last
// group ahead of nothing else. Ungrouped: one unlabeled group of placeable
// rows ranked by composite % (so only the trailing term'd separator, if any,
// renders). Grouped: one group per Ride.riderStatus (#821), alphabetical; a
// ride with no rider status sits in Open (#3405).
function buildPlacingGroups(results, ridesById, groupByDivision, t, catalogDefsById, classItem) {
  const placeable = [];
  const termd = [];
  for (const row of results) {
    (isBottomTierRow(row, ridesById) ? termd : placeable).push(row);
  }

  // #1420 — resolve the class's placing classType ONCE here so the display
  // ordering and the committed-placing suggestion can never disagree on
  // which USEF chain applies. Resolved over the placeable rows.
  const classType = resolveClassTypeForSuggestion(placeable, ridesById, catalogDefsById, classItem);

  const groups = [];
  if (groupByDivision) {
    const byDivision = groupRowsByDivision(placeable, ridesById, t);
    for (const key of orderedDivisionKeys(byDivision)) {
      const { label, rows } = byDivision.get(key);
      groups.push({ key, label, rows: sortByComposite(rows, classType) });
    }
  } else {
    groups.push({ key: "all", label: null, rows: sortByComposite(placeable, classType) });
  }

  if (termd.length > 0) {
    groups.push({
      key: TERMD_GROUP_KEY,
      label: t("placings.view.termd"),
      rows: sortByComposite(termd, classType),
    });
  }
  return groups;
}

// #599/#1079/#2852 — a class's placing status, derived from its
// getClassResults rows (the only per-class signal the API exposes;
// listClasses carries no aggregate). The "final but not yet placed"
// question is the shared classNeedsPlacings predicate (#2852) — the SAME
// formula the server's badge-counts route evaluates for the Classes-page badge,
// the header count chip and the nav badge (#3463/#3496; the FE no longer
// derives them client-side), so the two screens can never disagree again. getClassResults rows carry the judged status in `status`
// (FINAL / SCRATCHED / …), the predicate's own contract; see its doc
// comment in src/util/classNeedsPlacings.js for the full rule (all
// non-scratched rides FINAL — the #2113 server placing bar — with NO
// placing committed yet; #2852's driver decision supersedes #1079's
// "some placed, one blank still badges" rule, since a class with a
// terminated (eliminated/no-show/excused) ride would otherwise badge
// forever; placing 0 is a legitimate placing, #534; scratched rides
// excluded throughout, #920/#2840).
//
// #3464: for a class placed by division the same rule runs per division and the
// class badges while ANY division needs placings (still one class on the
// picker). The rows carry `riderStatus` (the rider's division) for such a class.
function deriveClassSummary(rows, classItem) {
  return {
    hasRides: rows.length > 0,
    // #3465: a placed group whose placings no longer match the current scores
    // counts as needing placings again (same chip, one class).
    finalUnplaced: classNeedsPlacings(
      rows,
      classItem?.placedByDivision === true,
      outOfDateOptions(rows, classItem)
    ),
  };
}

// #803 — list order: scheduled time ascending (earliest → latest), with the
// class-number comparator as a stable tie-break (equal/missing times).
// Classes missing placings no longer float to the top (#599's behavior) —
// they're called out with a badge instead (below), so the operator can work
// the show in ring order and still spot what's outstanding at a glance.
// scheduledTime is FVD's Z-stamped naive-local string, but Date.parse orders
// consistently across the show, so relative ordering is correct even though
// the absolute value is UTC.
function sortClassesForPlacings(classes) {
  return [...(classes ?? [])].sort((a, b) => {
    const at = a.scheduledTime ? Date.parse(a.scheduledTime) : NaN;
    const bt = b.scheduledTime ? Date.parse(b.scheduledTime) : NaN;
    const aOk = !Number.isNaN(at);
    const bOk = !Number.isNaN(bt);
    if (aOk && bOk && at !== bt) return at - bt; // earliest first
    if (aOk !== bOk) return aOk ? -1 : 1; // scheduled classes before unscheduled
    return compareByClassNumber(a, b);
  });
}

// #839 — batch mode: a BLANK box means "leave unplaced" (null). This differs
// from the old per-row auto-save flow (#534), where blank had to mean 0
// because blur-to-save couldn't otherwise distinguish "no edit" from "clear
// it" — a batch form doesn't have that ambiguity, so blank reads as its plain
// meaning. A typed "0" is still a legitimate placing (#534), never confused
// with blank/unplaced.
// #3278 -- a stable fingerprint of a class's committed placings, so the poll can
// tell "the server's placings differ from what the form shows" without diffing
// the whole results payload (percentages move during a live class; only the
// placings matter to the lock).
function placingsKeyOf(resultList) {
  return JSON.stringify(
    resultList
      .map((r) => [r.rideId, r.placing ?? null])
      .sort((x, y) => (x[0] < y[0] ? -1 : x[0] > y[0] ? 1 : 0))
  );
}

function parsePlacingInput(raw) {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) return { ok: false, value: null };
  return { ok: true, value: n };
}

// #3241 -- filters offered in the Add-filter dropdown. `key` is what
// `appliedFilters` holds; the filter rules live in `filteredClasses`.
const PLACINGS_FILTERS = [
  { key: "today", labelKey: "placings.todayOnly" },
  { key: "needsPlacings", labelKey: "placings.needsPlacingsOnly" },
];

export default function ClassPlacings() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.placings"));
  const scoreApi = useScoreApi();
  // scoreApi's identity is NOT stable across a background OIDC token refresh
  // (common-fe's useApiConfig rebuilds it whenever auth.user's access_token
  // changes, e.g. a silent renew) — a real operator can be mid-batch-entry
  // for many minutes. The effects below depend on stable primitives
  // (activeShowId/classId) and read scoreApi through this ref, so that churn
  // never reloads-and-wipes an in-progress Place entry out from under the
  // operator — exactly the class of silent data loss #839 exists to kill.
  const scoreApiRef = useRef(scoreApi);
  scoreApiRef.current = scoreApi;
  const { activeShowId, setActiveShowId, loading: loadingShow } = useActiveShow();

  // #2862 — placed-class lock. Once this class's placings are SET (any row
  // carries a committed placing — the same loose `placing != null` signal
  // classIsPlaced below and score-api's classHasCommittedPlacings both
  // use), the inputs + Place button go read-only for EVERYONE until an
  // override holder (score:override_placed_class — score_admin) presses the
  // explicit "Unlock to edit" action. The server enforces the same lock
  // (403 placed_class_locked on both placing write routes), so this is UX,
  // not authorization. Token-roles read mirrors ClassDetailDrawer's #2415
  // pattern (see src/permissions.js for why not common-fe's usePermission).
  const auth = useAuth();
  const { activeOrgId, isSuper } = useActiveOrg();
  const canOverridePlacedClass = useMemo(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken) return false;
    // #3414: a super admin holds every capability in every org.
    if (isSuper === true) return true;
    if (!activeOrgId) return false;
    try {
      const claims = jwtDecode(accessToken);
      const raw = claims.roles;
      const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
      return hasOverridePlacedClassPermission(map[activeOrgId], isSuper);
    } catch {
      return false;
    }
  }, [auth.user?.access_token, activeOrgId, isSuper]);
  const [overrideUnlocked, setOverrideUnlocked] = useState(false);

  // #1069 — an admin can be deep-linked here with ?classId=<id> (from a
  // scoresheet commit that unplaced a class, RideEditor's placing-impact
  // banner). Read through a ref, same churn-avoidance pattern as
  // scoreApiRef above: useSearchParams returns a new object on every
  // render, and this is only ever read once per class-list load below, not
  // something the effect should re-run for.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // #1290 — the same #1069 banner link also emits a &showId= (the ride's
  // own show), for the case where the operator's active show isn't the
  // unplaced class's show. Previously nothing here read it, so the deep
  // link silently no-oped into an empty class picker (no classId match,
  // no explanation) whenever the two shows differed. Switch the active
  // show once, on mount, to whatever the link asked for; if that id turns
  // out invalid (wrong org, bad id, ...) ActiveShowProvider's own repair
  // effect falls back to shows[0], so this can't strand the picker.
  const appliedShowIdParamRef = useRef(false);
  useEffect(() => {
    if (appliedShowIdParamRef.current) return;
    appliedShowIdParamRef.current = true;
    const requestedShowId = searchParamsRef.current.get("showId");
    if (requestedShowId && requestedShowId !== activeShowId) {
      setActiveShowId(requestedShowId);
    }
  }, [activeShowId, setActiveShowId]);

  const [classes, setClasses] = useState([]);
  // #3464: the summary of a class depends on its Placed by division setting, so
  // the handlers that refresh one class's summary read the class through a ref.
  const classesRef = useRef(classes);
  classesRef.current = classes;
  const summarizeClass = (id, rows) =>
    deriveClassSummary(rows, classesRef.current.find((c) => c.id === id));
  // #3660: classSummaries is now a LOCAL OVERRIDE map, filled only for classes
  // this page has loaded full results for (the open class, a class just placed
  // here). Every other class's "needs placings" comes from the server's one-call
  // badge-counts list (needsPlacingsClassIds). No per-class results fan-out.
  const [classSummaries, setClassSummaries] = useState({});
  const [classesLoading, setClassesLoading] = useState(false);
  const {
    finalClassLocksByClassId,
    needsPlacingsClassIds,
    loading: badgesLoading,
    error: badgesError,
    loadedTick: badgesLoadedTick,
  } = useBadgeCounts();
  // A local override is dropped once the server list AGREES with it (the badge
  // refresh after a Place has landed). While they disagree the override stays:
  // the list the server answered with may be a moment behind the write, and a
  // class just placed here must not pop back onto the Needs placings list.
  useEffect(() => {
    setClassSummaries((prev) => {
      const ids = Object.keys(prev);
      const agreed = ids.filter((id) => Boolean(prev[id].finalUnplaced) === needsPlacingsClassIds.has(id));
      if (agreed.length === 0) return prev;
      const next = { ...prev };
      for (const id of agreed) delete next[id];
      return next;
    });
  }, [badgesLoadedTick, needsPlacingsClassIds]);
  // Badges never loaded and the load failed: do not pretend nothing needs
  // placing. The Needs placings filter stands down and the chip is hidden.
  const badgesUnavailable = !badgesLoading && Boolean(badgesError) && badgesLoadedTick === 0;
  const classNeedsPlacingsNow = useCallback(
    (c) => {
      const local = classSummaries[c.id];
      if (local) return Boolean(local.finalUnplaced);
      return needsPlacingsClassIds.has(c.id);
    },
    [classSummaries, needsPlacingsClassIds],
  );
  const [classId, setClassId] = useState("");

  // #1994 — a class pick here was local React state only, never reflected
  // into the URL. Clicking through to a ride (RideLink -> /rides/:rideId)
  // and then hitting Back (RideEditor's `navigate(-1)`, #845) popped this
  // component back to its bare `/placings` URL, which remounts with no
  // `?classId=` to reselect (the effect below only reads it once per
  // classes load) — so the operator landed back on the unselected class
  // list ("main class placing screen") instead of the class they'd drilled
  // into. Mirroring the deep-link's own query param on every manual
  // selection keeps the URL in sync with what's on screen, so the history
  // entry `navigate(-1)` pops back to already carries the right class. This
  // PUSHes a real history entry per selection (default react-router-dom
  // behavior, same as ClassManager's per-class `/classes/:classId` Link) so
  // browser Back walks the picker's own history one class-pick at a time,
  // exactly as it already does for ClassManager — a `replace` here would
  // instead collapse the "nothing selected" root entry into whichever class
  // was picked first, leaving no history entry to pop back to underneath it.
  const selectClassId = useCallback(
    (nextClassId) => {
      setClassId(nextClassId);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (nextClassId) {
          next.set("classId", nextClassId);
        } else {
          next.delete("classId");
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // #3186 -- both flags are derived ("loaded for THIS id"), not set inside
  // their effects. A flag the effect flips is false on the first draw after the
  // id appears (and after a deep link's setClassId), so "no classes" / "no
  // results" flashed for a frame while the data was still in flight.
  const [loadingResults, markResultsLoaded] = useLoadedFor(classId);
  const [loadingClasses, markClassesLoaded] = useLoadedFor(activeShowId);
  const [error, setError] = useState(null);

  const [results, setResults] = useState([]);
  const [ridesById, setRidesById] = useState({});
  // Card #2063 AC3 — the SPECIFIC DR123.3 term reason per terminated ride
  // (Scratched/Excused/Eliminated/No Show), keyed by rideId. Computed
  // client-side via score-common's deriveRideTermReason (the single 4-tier
  // read path — see docs/score/term-reason-vocabulary.md), never
  // re-derived by hand here.
  const [termReasonsById, setTermReasonsById] = useState({});

  // #1665 — the LIVE score-catalog table (GET /catalog, card #1662's
  // useCatalogDefs / IndexedDB cache), replacing the retired build-time
  // bundle as the source getRideTestName/getCatalogTestById (classDisplay.js)
  // resolve a ride's test against.
  const { catalogDefs } = useCatalogDefs();
  const catalogDefsById = useMemo(() => {
    const map = new Map();
    for (const def of catalogDefs) map.set(def.id, def);
    return map;
  }, [catalogDefs]);

  // Server-derived badge status per placing row (#544/#546), polled live (#559)
  // so a ride flipping to FINAL/scored during the show updates without a manual
  // reload. The hook pauses polling while the tab is hidden.
  const rideIds = useMemo(() => results.map((r) => r.rideId).filter(Boolean), [results]);
  const {
    statuses: rideStatuses,
    termReasons: rideTermReasons = {},
    refresh: refreshRideStatuses,
  } = useRideStatuses(rideIds);
  const { classTick } = useShowChangeCheck(activeShowId, { classId: classId || undefined });
  useOnTick(classTick, () => {
    if (document.visibilityState !== "hidden") refreshRideStatuses();
  });

  // Per-row BATCH edit buffer: rideId -> the input's current string value.
  // Nothing here is sent to score-api until Place is pressed (#839) — reset
  // whenever results load/reload so the buffer always starts from the
  // server's current placings.
  const [editValues, setEditValues] = useState({});
  // #3278 -- what the form was last seeded from: the server's placings key
  // (rideId:placing pairs) and the seeded edit buffer. The placings poll below
  // compares a fresh read against these to tell "the class changed underneath
  // the open form" from "nothing happened", and the seeded buffer tells it
  // whether the admin had unsaved edits to warn about.
  const syncedPlacingsKeyRef = useRef(null);
  const syncedPlacedRef = useRef(false);
  const seededEditValuesRef = useRef({});
  const editValuesRef = useRef(editValues);
  editValuesRef.current = editValues;
  const [changedElsewhere, setChangedElsewhere] = useState(null);

  // Place-button submit state + the single banner that reports the outcome:
  // success (with a count), blocked (409 — class not releasable, names the
  // rides still holding it up), or a generic commit error.
  const [placeSubmitting, setPlaceSubmitting] = useState(false);
  // #3567 -- the "Place by the rules" preview/confirm dialog.
  const [placeByRulesOpen, setPlaceByRulesOpen] = useState(false);
  const placeSubmittingRef = useRef(false);
  placeSubmittingRef.current = placeSubmitting;
  const [placeBanner, setPlaceBanner] = useState(null);
  // #902 — inline resolve (force-final/scratch) on a single blocking ride
  // from the banner's list, without leaving the Placing screen. Tracks which
  // ride's action is in flight (disables just that row) plus a resolve-only
  // error, kept separate from placeBanner so a resolve failure doesn't stomp
  // the still-valid blocking list underneath it.
  const [resolvingRideId, setResolvingRideId] = useState(null);
  const [resolveError, setResolveError] = useState(null);
  // Card #1930 AC7 — the ride whose "No score recorded" reason dialog is open
  // from the blocking banner, plus that dialog's own error (kept apart from
  // resolveError so a refused mark, e.g. the ride already has marks, shows in
  // the dialog beside the reason the operator just typed).
  const [noScoreRideId, setNoScoreRideId] = useState(null);
  const [noScoreError, setNoScoreError] = useState(null);

  // Classes for the active show. The list renders as soon as listClasses
  // returns (#599 — no dropdown/extra action to see the classes); each class's
  // placing summary then loads in the background and re-sorts final-but-unplaced
  // classes to the top without blocking the initial render.
  useEffect(() => {
    setClassId("");
    setClasses([]);
    setClassSummaries({});
    setResults([]);
    setRidesById({});
    markClassesLoaded(null);
    setClassesLoading(Boolean(activeShowId));
    if (!activeShowId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const list = unwrap(await scoreApiRef.current.classes.listClasses(activeShowId));
        if (cancelled) return;
        // #1117 -- hidden classes are not offered in the placings list.
        const visibleList = filterVisibleClasses(list);
        setClasses(visibleList);
        markClassesLoaded(activeShowId);
        // #1069 — deep-link: only pre-select if the requested class
        // actually belongs to this (active) show's list. An unmatched or
        // absent classId leaves the picker exactly as it was (empty,
        // admin picks manually) — additive, not a behavior change.
        const requestedClassId = searchParamsRef.current.get("classId");
        if (requestedClassId && list.some((c) => c.id === requestedClassId)) {
          setClassId(requestedClassId);
        }
        setClassesLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setClassesLoading(false);
          markClassesLoaded(activeShowId);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, markClassesLoaded]);

  // #1994 — the effect above (and #1069's requestedClassId read inside it)
  // only reruns on an actual REMOUNT (activeShowId changing, or navigating
  // in from a different route entirely, e.g. RideEditor's Back). But
  // selectClassId pushes a real history entry per class pick, so browser
  // Back/Forward between two `/placings` entries that differ ONLY in
  // `?classId=` is a same-route popstate:
  // react-router updates `searchParams` without unmounting this component,
  // so without this effect `classId` state would go stale on exactly that
  // hop (verified failing before this effect was added: the SECOND of two
  // consecutive Backs left the previous class's heading on screen after the
  // URL had already reverted to no `classId`). Mirrors the deep-link
  // validation above: an unmatched/absent id is treated as "no selection",
  // never a dead invalid classId.
  useEffect(() => {
    const paramClassId = searchParams.get("classId") || "";
    if (paramClassId === classId) return;
    if (paramClassId && !classes.some((c) => c.id === paramClassId)) return;
    setClassId(paramClassId);
    // classId deliberately excluded: this effect reacts to the URL, not to
    // its own writes, and including it would fight selectClassId's own
    // setClassId call on every manual pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, classes]);

  const sortedClasses = useMemo(
    () => sortClassesForPlacings(classes),
    [classes],
  );
  // #803 — filterable/searchable class list, same match logic as ClassManager's
  // search (classNumberLabel + className, case-insensitive substring); also
  // matches ring since the list shows it as secondary text. The "Needs
  // placings only" toggle (product decision, follow-up to #803) composes with
  // search: default (off) keeps the full time-ordered list, on restricts to
  // classes that carry the "Needs placings" badge.
  //
  // #2855 — "today's classes only" (a class whose classDayKey equals
  // todayDayKey(), both the FVD naive-venue-wall-clock date domain — see
  // classDisplay.js) and "needs placings only" (summary.finalUnplaced, see
  // deriveClassSummary) are DEFAULT-ON filters composing with the same stack
  // (#3260 dropped the separate "hide already-placed" filter, needs-placings
  // covers it). Both initial values read the URL once on mount
  // (?today=0 / ?needsPlacings=0 opts out), same one-shot deep-link pattern as
  // #1069's ?classId= above — that keeps the pre-#2855 unfiltered list
  // reachable by link (and by the Cypress suites pinned to fixed-date
  // fixtures) without a race against the async per-class summary load.
  const [search, setSearch] = useState("");
  // #3241 -- the three filters are one list of applied keys (the shared
  // AddFilterMenu's controlled shape). today + needsPlacings start applied unless
  // the link opts out; removing a default-on chip turns that filter off.
  const [appliedFilters, setAppliedFilters] = useState(() => {
    const initial = [];
    if (searchParamsRef.current.get("today") !== "0") initial.push("today");
    if (searchParamsRef.current.get("needsPlacings") !== "0") initial.push("needsPlacings");
    return initial;
  });
  const needsPlacingsOnly = appliedFilters.includes("needsPlacings");
  const todayOnly = appliedFilters.includes("today");
  // Computed once per mount: the filter's "today" must not flip mid-session
  // while the operator works (a class list silently changing at midnight
  // would be worse than one that needs a reload).
  // #1814 AC3 -- a marked class is compared against "today" in ITS zone, so pin
  // the instant once and derive the key per class below.
  const mountedAt = useMemo(() => new Date(), []);
  const filteredClasses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortedClasses.filter((c) => {
      if (todayOnly && classDayKey(c) !== todayDayKey(mountedAt, c.scheduledTimeZone)) return false;
      if (needsPlacingsOnly && !badgesUnavailable && !classNeedsPlacingsNow(c)) return false;
      if (!q) return true;
      return `${classNumberLabel(c)} ${c.className ?? ""} ${c.ring ?? ""}`.toLowerCase().includes(q);
    });
  }, [sortedClasses, search, needsPlacingsOnly, badgesUnavailable, todayOnly, mountedAt, classNeedsPlacingsNow]);

  // #981 — the currently-selected class object, used by the placing screen's
  // top-right heading (see below): that heading is now the only linked
  // rendering of a class on this page (the picker's own entries are plain
  // text — see the ListItemText a few hundred lines down).
  const selectedClass = useMemo(
    () => classes.find((c) => c.id === classId) ?? null,
    [classes, classId],
  );
  // #3588: the server's lock for the Final Class Report (one definition of
  // current placings, shared with the Mid-Class Report); undefined = unlocked.
  const finalClassLock = selectedClass ? finalClassLocksByClassId.get(selectedClass.id) : undefined;

  // #3341 -- the class dropdown's anchor (null = closed), plus the prev/next
  // walk. Prev/next move through the FILTERED list in its sorted order, from
  // the selected class's place in the FULL sorted order, so a class that just
  // dropped out of the filtered list (e.g. it was placed while "Needs
  // placings" is on) still has a well-defined next/previous neighbour.
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const { prevClass, nextClass } = useMemo(() => {
    const at = sortedClasses.findIndex((c) => c.id === classId);
    if (at < 0) {
      return { prevClass: null, nextClass: filteredClasses[0] ?? null };
    }
    const pos = new Map(sortedClasses.map((c, i) => [c.id, i]));
    let prev = null;
    let next = null;
    for (const c of filteredClasses) {
      const i = pos.get(c.id);
      if (i < at) prev = c;
      else if (i > at && !next) next = c;
    }
    return { prevClass: prev, nextClass: next };
  }, [sortedClasses, filteredClasses, classId]);
  const needPlacingsCount = useMemo(
    () => filteredClasses.filter(classNeedsPlacingsNow).length,
    [filteredClasses, classNeedsPlacingsNow],
  );
  // #3660: the picker, arrows and chip never read as "nothing to place" while the
  // list they depend on is still loading. Classes load first; the Needs placings
  // filter additionally waits on the server's list.
  const pickerLoading = classesLoading || (needsPlacingsOnly && badgesLoading);

  // Results + rides (for labels) for the selected class. Re-run after a
  // successful Place commit (see handlePlace) so the table reflects the
  // freshly committed placings and re-sorts using the server's ordering.
  const loadResults = useCallback(async (id) => {
    // Reads scoreApi through the ref (see its doc comment above) so this
    // callback's own identity stays stable across an OIDC token refresh.
    const [resultList, rideList] = await Promise.all([
      scoreApiRef.current.results.getClassResults(id).then(unwrap),
      scoreApiRef.current.rides.listRides(id).then(unwrap),
    ]);
    // Card #2063 AC3 — one batched ScoreSheets call for the whole class's
    // rides (card #1961's listScoreSheetsForRides, the same batch endpoint
    // scribe-fe's useRidesForClass already uses), so deriveRideTermReason's
    // tier 4 (a lone sheet-level elimination not yet stamped onto
    // ride.terminationReason — the un-migrated #2056 legacy rows) resolves
    // correctly here too, not just tiers 1-3. Best-effort: a failure here
    // must not block the placings table itself from loading, so a fetch
    // error degrades to no sheets (tiers 1-3 still resolve every ride that
    // isn't relying on tier 4).
    const rideIds = rideList.map((r) => r.id);
    const sheetList = rideIds.length
      ? await scoreApiRef.current.scoreSheets
          .listScoreSheetsForRides({ rideIds })
          .then(unwrap)
          .catch(() => [])
      : [];
    return { resultList, rideList, sheetList };
  }, []);

  // Shared by the load effect below and handlePlace's post-commit refresh —
  // one place that turns a (resultList, rideList, sheetList) triple into the
  // table's results/ridesById/editValues/termReasonsById state.
  const applyResults = useCallback((resultList, rideList, sheetList = []) => {
    const map = Object.fromEntries(rideList.map((r) => [r.id, r]));
    setResults(sortResults(resultList, map));
    setRidesById(map);
    // Card #2063 AC3 — sheets grouped by rideId, then resolveDisplayTermReason
    // per ride (score-common's 4-tier deriveRideTermReason read path, PLUS
    // #2059/#2840's bare-SCRATCHED-with-no-recorded-reason fallback -- a
    // real, current production state, see project_term_reason_backfill_unrun).
    // Card #2840 -- this used to call deriveRideTermReason directly, which
    // misses that bare-SCRATCHED case entirely (its contract is deliberately
    // unchanged), so a scratched ride with no stamped reason got no chip, no
    // term-block on a stale placing, and (before this card) no score
    // suppression either. resolveDisplayTermReason is the same resolver
    // score-api/report-defs/report-api already standardized on for exactly
    // this reason. A ride with no term reason at all (never terminated) is
    // simply absent from the map rather than carrying an explicit null, so
    // callers can test presence with `termReasonsById[rideId]`.
    //
    // #2246 — hoisted ABOVE the setEditValues seed below, which now needs it.
    // Order is load-bearing, not cosmetic: see the pre-fill's own term-reason
    // gate for why.
    // #2974 — this batch read had no authority reference at all. Filtered
    // here (not left to resolveDisplayTermReason's own internal
    // filterAuthoritativeScoreSheets, score-common/utils/ride-status.js) so
    // every consumer of sheetsByRideId, present or future, sees only the
    // authoritative sheet per seat rather than depending on one particular
    // downstream function's own defensive filter.
    const sheetsByRideId = new Map();
    for (const sheet of authoritativeSheetsOnly(sheetList)) {
      if (!sheet?.rideId) continue;
      const list = sheetsByRideId.get(sheet.rideId);
      if (list) list.push(sheet);
      else sheetsByRideId.set(sheet.rideId, [sheet]);
    }
    const nextTermReasons = {};
    for (const ride of rideList) {
      const reason = resolveDisplayTermReason(ride, sheetsByRideId.get(ride.id) ?? []);
      if (reason) nextTermReasons[ride.id] = reason;
    }
    setTermReasonsById(nextTermReasons);
    // #2246 — pre-fill the Placing box from the place the JUDGE already wrote
    // in the sheet's own Place column (`judgeEnteredPlace`, #1752, carried on
    // every getClassResults row). A breed show is mostly place-only classes
    // (DSHB CHAMPIONSHIP/SUITABILITY, USEF DR204.2.c/DR210.5), which award NO
    // percentage at all, so the ranking-based suggestion this screen has always
    // shown has nothing to rank and the operator retypes, by hand, a number the
    // judge already recorded — duplicate entry on the exact path that releases
    // to FVD. DR211.5 (docs/rules/usef/rulebook-dr-dressage-2026.pdf, © USEF
    // 2026): "all scores (if given) and placings must be published".
    //
    // This changes NOTHING about what gets committed (Jaybill's 2026-09-03
    // ruling on the card: do the pre-fill, do NOT remove the admin placing
    // step). editValues is input state only; PUT /class/:classId/placings, run
    // by the PLACE button, is still the sole writer of Ride.placing, and the
    // operator can edit or clear a pre-filled box first. The admins already
    // know to press PLACE, so their workflow is unchanged.
    //
    // Three gates, in order:
    //  1. A COMMITTED Ride.placing always wins — the judge's sheet place never
    //     overwrites a placing that was already placed (0 included: #1785/#1839,
    //     a stored 0 is a real placing, not an absence, hence `!= null`).
    //  2. Only a POSITIVE integer seeds — matches score-common's own
    //     judgeEnteredPlaceOf and score-api's judgeEnteredPlaceFromSheets, so a
    //     stored 0 or a junk value is never promoted to a 1st place.
    //  3. A TERMINATED ride never seeds, even carrying a judge place. This is
    //     the #2057/#2069 trap the termBlockedEntries comment below describes,
    //     approached from the other side: a terminated ride cannot hold a real
    //     placing, so pre-filling one would hand the operator a row that is
    //     pre-blocked from placing through no action of their own, and 409 the
    //     whole batch on score-api's ride_terminated guard if it slipped past.
    //     nextTermReasons is why the block above had to be hoisted.
    const seeded = Object.fromEntries(
      resultList.map((r) => {
        if (r.placing != null) return [r.rideId, String(r.placing)];
        const judgePlace = r.judgeEnteredPlace;
        const seedable =
          typeof judgePlace === "number" &&
          Number.isInteger(judgePlace) &&
          judgePlace > 0 &&
          // #3365: a hors de concours ride never seeds a place (DR119.5).
          r.hc !== true &&
          !nextTermReasons[r.rideId];
        return [r.rideId, seedable ? String(judgePlace) : ""];
      })
    );
    seededEditValuesRef.current = seeded;
    syncedPlacingsKeyRef.current = placingsKeyOf(resultList);
    syncedPlacedRef.current = resultList.some((r) => r.placing != null);
    setEditValues(seeded);
  }, []);

  useEffect(() => {
    setResults([]);
    setRidesById({});
    setPlaceBanner(null);
    // #2862 — a class switch always re-locks: the override is per-class, never
    // carried over from whatever the operator unlocked last.
    setOverrideUnlocked(false);
    setChangedElsewhere(null);
    syncedPlacingsKeyRef.current = null;
    markResultsLoaded(null);
    if (!classId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const { resultList, rideList, sheetList } = await loadResults(classId);
        if (cancelled) return;
        applyResults(resultList, rideList, sheetList);
        // The open class is the one class whose full results are loaded here, so
        // its summary is exact; it overrides the server list until the next badge load.
        setClassSummaries((prev) => ({ ...prev, [classId]: summarizeClass(classId, resultList) }));
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) markResultsLoaded(classId);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classId, loadResults, applyResults, markResultsLoaded]);

  // #3278 -- re-read the selected class's placings when the change check says
  // the class moved (#3559; was a blind 10 s timer). The
  // form loads placings once; without this, a class placed underneath it (the
  // backend auto-place #3262, or another admin pressing Place) leaves an
  // editable, stale form that only fails at Place with a 403
  // placed_class_locked. When the server's placings differ from what the form
  // last synced, the form adopts them, re-locks (override reset, as a class
  // switch does) and says so, naming any unsaved edits it replaced. When
  // nothing differs it touches no state, so the edit buffer and the render are
  // left alone.
  const placingsRefreshRef = useRef(null);
  useOnTick(classTick, () => {
    if (document.visibilityState !== "hidden") placingsRefreshRef.current?.();
  });
  useEffect(() => {
    if (!classId) return undefined;
    let cancelled = false;
    let inFlight = false;
    const refresh = async () => {
      // Not yet loaded for this class, or our own Place is mid-commit (its own
      // refresh follows and would otherwise read as "changed elsewhere").
      if (inFlight || syncedPlacingsKeyRef.current == null || placeSubmittingRef.current) return;
      const keyAtStart = syncedPlacingsKeyRef.current;
      inFlight = true;
      try {
        const resultList = unwrap(await scoreApiRef.current.results.getClassResults(classId));
        if (cancelled || placeSubmittingRef.current) return;
        // The form re-synced itself (own Place, resolve) while this read was
        // in flight: this read is older than the form, drop it.
        if (syncedPlacingsKeyRef.current !== keyAtStart) return;
        if (placingsKeyOf(resultList) === keyAtStart) return;
        const wasPlaced = syncedPlacedRef.current;
        const nowPlaced = resultList.some((r) => r.placing != null);
        const seeded = seededEditValuesRef.current;
        const current = editValuesRef.current;
        const hadUnsaved = Object.keys({ ...seeded, ...current }).some(
          (k) => (current[k] ?? "") !== (seeded[k] ?? "")
        );
        const { resultList: freshResults, rideList, sheetList } = await loadResults(classId);
        if (cancelled || placeSubmittingRef.current) return;
        // An own Place/resolve re-synced the form while loadResults was in
        // flight: this read is older than the form, drop it.
        if (syncedPlacingsKeyRef.current !== keyAtStart) return;
        applyResults(freshResults, rideList, sheetList);
        setClassSummaries((prev) => ({ ...prev, [classId]: summarizeClass(classId, freshResults) }));
        setOverrideUnlocked(false);
        setChangedElsewhere({
          kind: nowPlaced ? (wasPlaced ? "changed" : "placed") : "unplaced",
          hadUnsaved,
        });
      } catch {
        // A failed background read changes nothing; the next tick retries.
      } finally {
        inFlight = false;
      }
    };
    placingsRefreshRef.current = refresh;
    return () => {
      cancelled = true;
      if (placingsRefreshRef.current === refresh) placingsRefreshRef.current = null;
    };
  }, [classId, loadResults, applyResults]);

  // #3261/#3405 — group by division ONLY when the class is set to be placed by
  // division (Class.placedByDivision, DR126.2.a.3), the same field the backend
  // auto-placer obeys. No operator toggle on this page.
  const groupByDivision = selectedClass?.placedByDivision === true;

  // Live-parsed view of every row's edit buffer — recomputed as the operator
  // types, so both the Place button and each row's TextField reflect validity
  // without a network round-trip (#839 — batch entry, no per-row save).
  const parsedEntries = useMemo(
    () => results.map((r) => ({ rideId: r.rideId, ...parsePlacingInput(editValues[r.rideId]) })),
    [results, editValues]
  );
  const invalidRideIds = useMemo(
    () => new Set(parsedEntries.filter((e) => !e.ok).map((e) => e.rideId)),
    [parsedEntries]
  );

  // #1999 (AC5): display-only tie-break signal column. Which of
  // row.collectiveTotal / row.artisticTotal (if either) is the number
  // DR123.3/DR129.5 actually use to break a percentage tie for this class's
  // classType, per score-common's tieBreakDisplayFieldForClassType (see
  // that export's comment for why this is exactly two buckets, not three).
  // Resolved off the class's real classType the same way the needs-human reason
  // below does (resolveClassTypeForSuggestion), so a Freestyle class shows
  // Artistic Marks and an Equitation/FEI Children class shows no column at
  // all (score sheets and rules for those carry neither number).
  // DISPLAY ONLY: this memo is never read by handlePlace,
  // compareByComposite, or tieIndicatorsFor, so it cannot affect ordering.
  const tieBreakField = useMemo(
    () =>
      tieBreakDisplayFieldForClassType(
        resolveClassTypeForSuggestion(results, ridesById, catalogDefsById, selectedClass)
      ),
    [results, ridesById, catalogDefsById, selectedClass]
  );

  // #2159 — surfaces #1793's (and #2220's) NON-blocking `judgeFloorWarning`
  // (a Medal class, USEF DR133.2.g.3, or a USDFBC Series Final, USEF DR211.2,
  // staffed with fewer judges than the rule's floor). The API stamps the same
  // warning (or null) on every ride result of a class, so the first non-null
  // one stands for the class. Everything shown (count, floor, rule, message)
  // is the API's own: the rule figure is never recomputed here. Advisory only:
  // never read by handlePlace or any gate (Jaybill, #1793, 2026-08-30).
  const judgeFloorWarning = useMemo(
    () => results.find((r) => r?.judgeFloorWarning)?.judgeFloorWarning ?? null,
    [results]
  );

  // #3368 -- WHY a panel-consensus class (Dressage Seat) can never reach FINAL.
  // isPanelFullySigned fails closed when the class's expected judge count is
  // outside the test def's [minJudges, maxJudges] (a DSM class with 4 judges
  // against USEF DR133.2.g.3's three), which used to be silent. Unlike the
  // advisory floor notice above this one IS blocking: the class genuinely
  // cannot finalize, so it is an error-severity alert that names the fix, the
  // expected-judges override on the class (#3351). Computed client-side from
  // score-common's own predicate (the same one the server gate calls), off the
  // class's expected count (override aware) and the loaded catalog def of its
  // rides' tests, so the bound shown is the def's, never recomputed here.
  const panelSizeViolation = useMemo(() => {
    if (!selectedClass) return null;
    const expected = expectedJudgeCountForClass(selectedClass);
    for (const r of results) {
      const test = getCatalogTestById(ridesById[r?.rideId]?.testId, catalogDefsById);
      const violation = panelSizeBoundsViolation(test, expected);
      if (violation) return violation;
    }
    return null;
  }, [results, ridesById, catalogDefsById, selectedClass]);

  // #2163 — surfaces the NON-blocking `signatureWarning` (USEF DR123.1: scores
  // must be signed before being tabulated). The API stamps it per ride result
  // when a seat is confirmed but not yet signed; one banner for the class, the
  // message is the API's own (never recomputed here). Advisory only: never read
  // by handlePlace or any gate (Jaybill, 2026-10-04: warn, no gate).
  const signatureWarning = useMemo(
    () => results.find((r) => r?.signatureWarning)?.signatureWarning ?? null,
    [results]
  );

  // #3261 — the grouped/sorted display rows (see buildPlacingGroups' doc
  // comment). Recomputed from `results`, never written back to it.
  const displayGroups = useMemo(
    () => buildPlacingGroups(results, ridesById, groupByDivision, t, catalogDefsById, selectedClass),
    [results, ridesById, groupByDivision, t, catalogDefsById, selectedClass]
  );

  // #3261 AC5 — why a class the backend auto-placer (#3262) leaves for a human
  // needs one: an unbroken tie, a judge-entered class with a ride missing a
  // place or two sharing one, or a Medal tie owing a ride-off (DR133.2.g.5).
  // Judged with the SAME score-common suggestion engine the auto-placer runs,
  // so the page names exactly the cases the backend skips. Only a fully-FINAL
  // class with nothing placed yet (classNeedsPlacings) is a skipped class:
  // a placed class is already done, and a class still being scored is not
  // waiting on a human. Display only: never read by handlePlace.
  const needsHumanReason = useMemo(() => {
    if (!classNeedsPlacings(results)) return null;
    // #3365: an HC ride takes no place (USEF DR119.5), so it is neither a
    // missing judge place nor a tie candidate.
    const placeableGroups = displayGroups
      .filter((g) => g.key !== TERMD_GROUP_KEY)
      .map((g) => g.rows.filter((r) => r.hc !== true));
    const placeable = placeableGroups.flat();
    const classType = resolveClassTypeForSuggestion(placeable, ridesById, catalogDefsById, selectedClass);
    const { suggestions } = suggestedPlacingsByDivision(
      placeable,
      ridesById,
      catalogDefsById,
      selectedClass,
      t,
      classType
    );
    return placingNeedsHumanReason({ groups: placeableGroups, suggestions, classType });
  }, [results, displayGroups, ridesById, catalogDefsById, selectedClass, t]);

  // #3261 AC4 — the table's header columns, built once. The header row
  // renders from this list and a group bar's colSpan is its length, so the
  // bar spans every rendered column by construction (it used to be a
  // hand-kept (SUGGESTED ? 6 : 5) + tieBreak and stopped one short). A
  // division-grouped class drops the Division column, the gray bar already
  // names the division; an ungrouped class keeps it. The body cells render
  // under the same two flags (tieBreakField, groupByDivision).
  const headerColumns = [
    { key: "placing", label: t("placings.columns.placing") },
    { key: "cno", label: t("placings.columns.cno") },
    { key: "riderFirstName", label: t("placings.columns.riderFirstName") },
    { key: "riderLastName", label: t("placings.columns.riderLastName") },
    { key: "horse", label: t("placings.columns.horse") },
    { key: "test", label: t("placings.columns.test") },
    { key: "composite", label: t("placings.columns.composite") },
    tieBreakField && {
      key: "tiebreak",
      label: t(TIE_BREAK_COLUMN_LABEL_KEYS[tieBreakField]),
      testid: "placings-tiebreak-header",
    },
    { key: "status", label: t("placings.columns.status") },
    !groupByDivision && { key: "division", label: t("placings.columns.division") },
  ].filter(Boolean);

  // #904 — duplicate-placing guard: nothing previously stopped the same
  // placing value from being entered on two different rides (#898 saw two
  // rides both land on placing 1 with no warning before Place committed and
  // released to FVD). Bottom-tier rows (scratched/eliminated, #811 — never
  // competitively placed) are excluded so their blank/irrelevant edit values
  // can't false-positive a collision. Blank stays "unplaced" and never
  // participates. Grouped by value so the banner can name exactly which
  // rides collide on which placing.
  // #945/#2049/#3261 — in a division-grouped class the same placing
  // legitimately repeats once per division (e.g. every division's own 1st
  // place), so the check is scoped WITHIN each division group: same division +
  // same placing is an entry error and warns/blocks; a cross-division repeat
  // stays silent. The scope key is divisionKeyForRow's normalized key, the
  // same one the displayed grouping uses, so the guard always matches the
  // groups on screen. An ungrouped class keeps the flat class-wide check.
  const duplicatePlacingGroups = useMemo(() => {
    const byScope = new Map();
    for (const row of results) {
      if (isBottomTierRow(row, ridesById)) continue;
      const entry = parsedEntries.find((e) => e.rideId === row.rideId);
      if (!entry?.ok || entry.value == null) continue;
      const scope = groupByDivision
        ? `${divisionKeyForRow(row, ridesById)}::${entry.value}`
        : entry.value;
      const group = byScope.get(scope) ?? { value: entry.value, rideIds: [] };
      group.rideIds.push(row.rideId);
      byScope.set(scope, group);
    }
    // #1420 / AC3 — a shared placing that IS a legitimate unbreakable tie
    // (the comparator can't separate the rides) is the rule-mandated way to
    // represent that tie, NOT an entry error, so it must not warn. Resolve the
    // class's chain once and drop every duplicate group whose rides are all
    // genuinely tied; a shared placing the comparator DOES rank (or that
    // includes an unscored ride) stays a collision and still warns.
    // Same input buildPlacingGroups resolves its classType over (the placeable
    // rows, bottom-tier excluded), so the warning and the displayed ordering
    // can never disagree on which chain applies.
    const placeableRows = results.filter((r) => !isBottomTierRow(r, ridesById));
    const classType = resolveClassTypeForSuggestion(placeableRows, ridesById, catalogDefsById, selectedClass);
    const rowByRideId = new Map(results.map((r) => [r.rideId, r]));
    return [...byScope.values()]
      .filter((g) => g.rideIds.length > 1)
      .filter((g) => !isLegitimateTieGroup(g.rideIds.map((id) => rowByRideId.get(id)), classType))
      .sort((a, b) => a.value - b.value);
  }, [results, ridesById, parsedEntries, groupByDivision, catalogDefsById, selectedClass]);
  const duplicatePlacingRideIds = useMemo(
    () => new Set(duplicatePlacingGroups.flatMap((g) => g.rideIds)),
    [duplicatePlacingGroups]
  );

  // #3415 -- Places awarded (Class.placesAwarded, an explicit positive integer)
  // is how many places the show ribbons. A hand-entered place above it is
  // almost always a slip (the extra riders should be blank or 0), so it warns
  // before Place commits, with the same "Place anyway" exit as a duplicate
  // (the secretary may genuinely want it). Unset means no limit and never
  // warns, and an Equitation / Medal class's default of 8 is not an explicit
  // value so it does not warn either. In a Placed by division class the limit
  // is per division, which is already how the typed values read (each division
  // has its own 1st), so a plain "value > N" is the right test.
  const placesAwardedLimit = explicitPlacesAwarded(selectedClass?.placesAwarded);
  const overPlacesAwardedEntries = useMemo(() => {
    if (placesAwardedLimit === null) return [];
    return parsedEntries
      .filter((e) => e.ok && e.value != null && e.value > placesAwardedLimit)
      .filter((e) => {
        const row = results.find((r) => r.rideId === e.rideId);
        return row && !isBottomTierRow(row, ridesById);
      })
      .map((e) => ({ rideId: e.rideId, value: e.value }));
  }, [placesAwardedLimit, parsedEntries, results, ridesById]);

  // #2057 -- client-side pre-check for the #2069 server-side hard block: a
  // ride carrying a resolved term reason (termReasonsById, deriveRideTermReason's
  // 4-tier read path, populated in applyResults) cannot hold a REAL placing
  // (0 included, per #1785/#1839 -- 0 is a stored placing, not an absence).
  // The trap this closes: applyResults seeds editValues from each ride's
  // STORED placing, so a legacy ride that is terminated but still carries an
  // old placing value keeps resubmitting that number on every Place press,
  // 409ing the entire batch with score-api's ride_terminated guard (results.js
  // ~line 1386) and no per-row indication of which ride is at fault. This
  // catches it locally before the network round-trip, naming the specific
  // row(s) and their specific DR123.3 outcome (AC1/AC2) -- unlike
  // duplicatePlacingGroups this has NO skip/override: Jaybill's 2026-08-30
  // decision on #2057/#2069 rejected warn-and-allow outright, so there is no
  // "place anyway" path here, only "blank the row and place again".
  const termBlockedEntries = useMemo(() => {
    const list = [];
    for (const entry of parsedEntries) {
      if (!entry.ok || entry.value == null) continue;
      const termReason = termReasonsById[entry.rideId];
      if (termReason) list.push({ rideId: entry.rideId, termReason });
    }
    return list;
  }, [parsedEntries, termReasonsById]);
  const termBlockedRideIds = useMemo(
    () => new Set(termBlockedEntries.map((e) => e.rideId)),
    [termBlockedEntries]
  );

  // #2862 — is the SELECTED class already placed? The loose "any committed
  // placing" signal (the loose `placing != null` rule on any row, and what
  // score-api's classHasCommittedPlacings gates on): a partially-placed class
  // is a placed class for lock purposes. Computed off the live `results` (not
  // classSummaries) so it flips the instant a Place commit reloads the table.
  //
  // #3464: the lock is per PLACING GROUP, the same grouping score-api's lock
  // uses (score-common placing-groups.js). In a class placed by division each
  // division is its own group: a division that already holds a placing is
  // read-only, a division with none stays editable for a normal placer. A class
  // that is not placed by division is one group, so it locks as a whole.
  //
  // The lock, the "placed divisions" labels and the "unplaced divisions" labels
  // all read the division from the RESULT ROWS (getClassResults stamps
  // riderStatus on each row of a class placed by division), so the three can
  // never disagree about which division a row belongs to.
  const groupKeyOfRow = (row) => placingGroupKeyOf(row, groupByDivision);
  const lockedGroupKeys = useMemo(
    () => lockedPlacingGroupKeys(results, groupByDivision),
    [results, groupByDivision]
  );
  const classIsPlaced = lockedGroupKeys.size > 0;
  // Read-only for EVERYONE until an override holder explicitly unlocks; a
  // non-admin never sees the unlock action, so for them a placed group is
  // simply, permanently read-only (AC1).
  const isRowLocked = (row) => !overrideUnlocked && lockedGroupKeys.has(groupKeyOfRow(row));
  const lockedRideIds = new Set(results.filter(isRowLocked).map((r) => r.rideId));
  // Every row read-only: the Place button has nothing left to commit.
  const placingsLocked = results.length > 0 && lockedRideIds.size === results.length;
  // Names of the divisions that are placed (read-only) while others are not.
  const lockedDivisionLabels = groupByDivision
    ? placingGroupsOf(results, true)
        .filter((g) => lockedGroupKeys.has(g.key))
        .map((g) => g.division ?? t("placings.view.open"))
    : [];
  // #3465: the rides that would move if the class were re-placed from its
  // current scores (old place, new place). Empty when the placings are current.
  const outOfDateRideMoves = useMemo(
    () => outOfDateMoves(results, selectedClass),
    [results, selectedClass]
  );
  // #3464: the divisions still to place, for the class page.
  const unplacedDivisionLabels = divisionsNeedingPlacings(results, groupByDivision).map(
    ({ division }) => division ?? t("placings.view.open")
  );

  // The single Place action (#839): validates every row locally first (no
  // network call for a plain format error), then commits the WHOLE class in
  // one atomic request. score-api itself re-validates + re-checks
  // releasability server-side and blocks (409, nothing written) if the class
  // isn't fully releasable yet — that's the authoritative gate, this is just
  // a fast local pre-check. #904 — a duplicate-placing collision warns
  // instead of silently committing; the operator can press "Place anyway"
  // (skipDuplicateCheck) to proceed, since legitimate ties do happen.
  const handlePlace = async ({ skipDuplicateCheck = false, skipOverPlacesCheck = false } = {}) => {
    // #2862 — defense in depth behind the disabled Place button: a locked
    // class never commits locally either (the server would 403 it anyway).
    if (placingsLocked) return;
    if (invalidRideIds.size > 0) {
      setPlaceBanner({ severity: "error", message: t("placings.fixInvalidBeforePlacing") });
      return;
    }
    // #2057 -- checked before the duplicate-placing warning (and with no
    // skip flag, unlike that one): a resolved term reason is a hard block
    // per Jaybill's 2026-08-30 decision, never a warn-and-allow. Catching it
    // here means the class-wide error names every offending row up front
    // instead of surfacing only after the whole batch 409s server-side.
    if (termBlockedEntries.length > 0) {
      setPlaceBanner({
        severity: "error",
        message: t("placings.termBlockedWarning"),
        termBlockedEntries,
      });
      return;
    }
    if (!skipOverPlacesCheck && overPlacesAwardedEntries.length > 0) {
      setPlaceBanner({
        severity: "warning",
        message: t("placings.overPlacesAwardedWarning", { count: placesAwardedLimit }),
        overPlacesAwarded: overPlacesAwardedEntries,
      });
      return;
    }
    if (!skipDuplicateCheck && duplicatePlacingGroups.length > 0) {
      setPlaceBanner({
        severity: "warning",
        message: t("placings.duplicatePlacingWarning"),
        duplicateGroups: duplicatePlacingGroups,
      });
      return;
    }
    setPlaceSubmitting(true);
    placeSubmittingRef.current = true;
    setPlaceBanner(null);
    setChangedElsewhere(null);
    try {
      const payload = {
        // #3464: a row in a placed (read-only) group is never sent; the server
        // refuses any entry for it without the override.
        placings: parsedEntries
          .filter(({ rideId }) => !lockedRideIds.has(rideId))
          .map(({ rideId, value }) => ({ rideId, placing: value })),
      };
      const data = unwrap(await scoreApi.results.setClassPlacings(classId, payload));
      setPlaceBanner({
        severity: "success",
        message: t("placings.placeSuccess", { count: data.placed }),
      });
      // #822 attaches here — see the function's own doc comment above.
      await triggerScoresheetGenAndEmail(classId, data);

      const { resultList, rideList, sheetList } = await loadResults(classId);
      applyResults(resultList, rideList, sheetList);
      setClassSummaries((prev) => ({ ...prev, [classId]: summarizeClass(classId, resultList) }));
      // #2862 — the override is per-CHANGE, not per-session: a successful
      // commit re-locks the class, so the next edit needs another explicit
      // unlock (and can't ride along silently on a stale override).
      setOverrideUnlocked(false);
    } catch (err) {
      // The shared axios interceptor (common-fe) rewrites err.message to a
      // generic "Server Error" so raw backend text never leaks by accident —
      // the curated 409/400 messages below read err.response directly instead,
      // which is the documented escape hatch for exactly this case.
      const status = err?.response?.status;
      const failData = err?.response?.data?.data;
      // #2057 -- score-api's setClassPlacings (results.js ~line 1386) 409s
      // with a DIFFERENT payload shape (code ride_terminated, a
      // `terminatedRides` array) than the pre-existing releasability 409
      // (code not_releasable, `blockingRideIds`/`blockingRides`) checked
      // below. This is the client-side pre-check's defense-in-depth
      // fallback (a race between load and submit, or a reason only the
      // server's own fetchAllSheetsForRide call resolves) -- the pre-check
      // above is expected to catch the common case before the request ever
      // goes out.
      if (status === 409 && failData?.terminatedRides?.length > 0) {
        setPlaceBanner({
          severity: "error",
          message: failData?.message || t("placings.termBlockedWarning"),
          termBlockedEntries: failData.terminatedRides,
        });
      } else if (status === 409) {
        setPlaceBanner({
          severity: "warning",
          message: failData?.message || t("placings.placeBlockedHeading"),
          blockingRideIds: failData?.blockingRideIds || [],
          // #902 — richer {rideId, wasEverConfirmed} shape (falls back to
          // undefined on an older server response; the banner render then
          // derives an all-false list from blockingRideIds instead).
          blockingRides: failData?.blockingRides,
        });
      } else if (status === 400) {
        setPlaceBanner({ severity: "error", message: failData?.message || t("placings.placeError") });
      } else {
        setPlaceBanner({ severity: "error", message: t("placings.placeError") });
      }
    } finally {
      setPlaceSubmitting(false);
    }
  };

  // #3567 -- a "Place by the rules" confirm went through: same refresh as a
  // hand Place (reload the table, re-lock the class, update the class summary).
  const handlePlacedByRules = async (data) => {
    setPlaceByRulesOpen(false);
    setPlaceBanner({
      severity: "success",
      message: t("placings.placeSuccess", { count: data?.placed ?? 0 }),
    });
    setChangedElsewhere(null);
    try {
      const { resultList, rideList, sheetList } = await loadResults(classId);
      applyResults(resultList, rideList, sheetList);
      setClassSummaries((prev) => ({ ...prev, [classId]: summarizeClass(classId, resultList) }));
      setOverrideUnlocked(false);
    } catch {
      setPlaceBanner({ severity: "error", message: t("placings.placeError") });
    }
  };

  // #902 — resolve a single blocking ride inline, right from the banner.
  // #1268 removed the "forceFinal" branch this used to offer for a
  // reopened-but-previously-confirmed ride (finalizeRide is gone entirely);
  // "scratch" mirrors RideEditor's AC6 scratch toggle — for a ride that
  // genuinely never happened. Reloads the table + optimistically drops the
  // resolved ride out of the banner's blocking list (rather than
  // re-submitting Place) so the operator can resolve several blockers in a
  // row before pressing Place again.
  const handleResolveBlockingRide = async (rideId, action, reason) => {
    setResolvingRideId(rideId);
    setResolveError(null);
    setNoScoreError(null);
    try {
      if (action === "scratch") {
        await scoreApi.rides.setRideStatus(rideId, { status: RIDE_STATUSES.SCRATCHED });
      } else if (action === "noScore") {
        // Card #1930 — the honest third exit: the ride rode but has no score.
        // Recorded with a required reason instead of inventing marks.
        await scoreApi.rides.markRideNoScoreRecorded(rideId, { reason });
        setNoScoreRideId(null);
      }
      const { resultList, rideList, sheetList } = await loadResults(classId);
      applyResults(resultList, rideList, sheetList);
      setClassSummaries((prev) => ({ ...prev, [classId]: summarizeClass(classId, resultList) }));
      setPlaceBanner((prev) => {
        if (!prev?.blockingRides?.length) return prev;
        const blockingRides = prev.blockingRides.filter((r) => r.rideId !== rideId);
        if (blockingRides.length === 0) return null;
        return { ...prev, blockingRides, blockingRideIds: blockingRides.map((r) => r.rideId) };
      });
    } catch (err) {
      // The no-score dialog is open for this ride: its own refusal (reason
      // missing, ride already holds marks) belongs beside the reason field.
      if (action === "noScore") setNoScoreError(err);
      else setResolveError(err);
    } finally {
      setResolvingRideId(null);
    }
  };

  if (loadingShow) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom color="text.primary">
          {t("placings.title")}
        </Typography>
        <TableSkeleton rows={4} columns={2} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("placings.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("placings.loadError")}
        </Alert>
      )}

      {!activeShowId && (
        <Typography color="text.secondary">{t("ring.noShows")}</Typography>
      )}

      {activeShowId && loadingClasses && <TableSkeleton rows={5} columns={2} />}

      {activeShowId && !loadingClasses && classes.length === 0 && (
        <Typography color="text.secondary">{t("placings.noClasses")}</Typography>
      )}

      {activeShowId && !loadingClasses && classes.length > 0 && (
        <Stack direction="column" spacing={3} sx={{ mb: 3 }}>
          {/* #3341 -- the class picker is ONE bar above the table (it was a
              fixed 360px left column that starved the placings table on a
              laptop): a combobox button showing the selected class
              (number + name + ring + time) that opens the same searchable,
              filterable, time-sorted list as before (#803, #3241), prev/next
              buttons that walk the filtered list, a count of classes still
              needing placings, and the #981 class link as "Open class". */}
          <Box
            sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}
            data-testid="placings-class-bar"
          >
            <IconButton
              size="small"
              aria-label={t("placings.previousClass")}
              onClick={() => prevClass && selectClassId(prevClass.id)}
              disabled={!prevClass}
              data-testid="placings-class-prev"
            >
              <ChevronLeftIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="inherit"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={pickerAnchor ? "true" : "false"}
              aria-label={t("placings.classPicker")}
              onClick={(e) => setPickerAnchor(e.currentTarget)}
              endIcon={<ExpandMoreIcon />}
              data-testid="placings-class-select"
              sx={{
                flexGrow: 1,
                minWidth: 0,
                maxWidth: 640,
                justifyContent: "space-between",
                textAlign: "left",
                textTransform: "none",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                {selectedClass ? (
                  <>
                    <Typography variant="body1" noWrap data-testid="placings-selected-class-heading">
                      {classNumberLabel(selectedClass)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      data-testid="placings-selected-class-meta"
                    >
                      {selectedClass.ring
                        ? t("ring.ringLabel", { ring: selectedClass.ring })
                        : t("ring.unassignedRing")}
                      {" · "}
                      {formatClassStartTime(selectedClass.scheduledTime, selectedClass.scheduledTimeZone) || t("placings.noTime")}
                    </Typography>
                  </>
                ) : pickerLoading ? (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    data-testid="placings-class-select-loading"
                  >
                    <CircularProgress size={16} aria-hidden="true" />
                    {t("placings.loadingClasses")}
                  </Typography>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    {t("placings.selectClassPrompt")}
                  </Typography>
                )}
              </Box>
            </Button>
            <IconButton
              size="small"
              aria-label={t("placings.nextClass")}
              onClick={() => nextClass && selectClassId(nextClass.id)}
              disabled={!nextClass}
              data-testid="placings-class-next"
            >
              <ChevronRightIcon />
            </IconButton>
            {badgesLoading ? (
              <Chip
                size="small"
                icon={<CircularProgress size={14} aria-label={t("placings.loadingClasses")} />}
                label=""
                data-testid="placings-needs-placings-loading"
              />
            ) : (
              !badgesUnavailable && (
                <Chip
                  size="small"
                  color={needPlacingsCount > 0 ? "warning" : "default"}
                  label={t("placings.classesNeedPlacings", { count: needPlacingsCount })}
                  data-testid="placings-needs-placings-count"
                />
              )
            )}
            {selectedClass && (
              <Button
                size="small"
                component={RouterLink}
                to={`/classes/${selectedClass.id}`}
                data-testid="placings-open-class"
              >
                {t("placings.openClass")}
              </Button>
            )}
            {/* #3586 -- Related reports quick pick: one link per entry in
                RELATED_CLASS_REPORTS, each opening ReportsPage with this
                class preselected and the report auto-run. */}
            {selectedClass && (
              <Box
                sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}
                data-testid="placings-related-reports"
              >
                <Typography variant="body2" color="text.secondary">
                  {t("placings.relatedReports")}
                </Typography>
                {RELATED_CLASS_REPORTS.map((r) =>
                  r.lockable && finalClassLock ? (
                    // #3588: locked, not a link; the reason and fix follow.
                    <Button
                      key={r.key}
                      size="small"
                      disabled
                      startIcon={<LockOutlinedIcon fontSize="small" />}
                      data-testid={`placings-related-report-${r.key}`}
                    >
                      {t(r.labelKey)}
                    </Button>
                  ) : (
                    <Button
                      key={r.key}
                      size="small"
                      component={RouterLink}
                      to={relatedReportPath(r.reportId, selectedClass.showId || activeShowId, selectedClass.id)}
                      data-testid={`placings-related-report-${r.key}`}
                    >
                      {t(r.labelKey)}
                    </Button>
                  )
                )}
                {finalClassLock && <FinalClassLockNotice lock={finalClassLock} classId={selectedClass.id} />}
              </Box>
            )}
          </Box>
          <Popover
            open={Boolean(pickerAnchor)}
            anchorEl={pickerAnchor}
            onClose={() => setPickerAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { width: 420, maxWidth: "calc(100vw - 32px)", p: 1.5 } } }}
          >
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={t("ring.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="placings-search"
              sx={{ mb: 1 }}
            />
            {/* #3241 -- Add-filter dropdown + removable chips (shared
                AddFilterMenu, #3240) replacing the #2855 "Today only" /
                "Hide placed" and #803 "Needs placings only" switches, whose
                labels wrapped. Rules unchanged; today + needsPlacings start
                applied (see the state declarations above). */}
            <Box sx={{ mb: 1 }}>
              <AddFilterMenu
                testIdPrefix="placings-filter"
                filters={PLACINGS_FILTERS.map((f) => ({ key: f.key, label: t(f.labelKey) }))}
                applied={appliedFilters}
                onChange={setAppliedFilters}
              />
            </Box>
            <List
              dense
              data-testid="placings-class-list"
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                maxHeight: 420,
                overflow: "auto",
              }}
            >
              {pickerLoading && (
                <Typography
                  color="text.secondary"
                  sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}
                  data-testid="placings-classes-loading"
                >
                  <CircularProgress size={16} aria-hidden="true" />
                  {t("placings.loadingClasses")}
                </Typography>
              )}
              {!pickerLoading && filteredClasses.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 2 }} data-testid="placings-no-filtered-results">
                  {t("placings.noSearchResults")}
                </Typography>
              )}
              {!pickerLoading && filteredClasses.map((c) => {
                const needsPlacing = classNeedsPlacingsNow(c);
                const time = formatClassStartTime(c.scheduledTime, c.scheduledTimeZone) || t("placings.noTime");
                const secondary = c.ring
                  ? `${t("ring.ringLabel", { ring: c.ring })} · ${time}`
                  : time;
                return (
                  <ListItemButton
                    key={c.id}
                    selected={c.id === classId}
                    onClick={() => {
                      selectClassId(c.id);
                      setPickerAnchor(null);
                    }}
                    data-testid={`placings-class-${c.id}`}
                  >
                    <ListItemText primary={classNumberLabel(c)} secondary={secondary} />
                    {needsPlacing && (
                      <Chip
                        size="small"
                        color="warning"
                        label={t("placings.needsPlacings")}
                        data-testid={`placings-needs-placings-${c.id}`}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Popover>

          <Box sx={{ minWidth: 0 }}>
            {!classId && (
              <Typography color="text.secondary">
                {t("placings.selectClassPrompt")}
              </Typography>
            )}

            {classId && !loadingResults && results.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("placings.batchHelp")}
              </Typography>
            )}

            {/* #3261 AC5 — a fully-final class nothing has placed yet is one
                the backend auto-placer (#3262) skipped; say why a human has to
                place it. Only the four cases the auto-placer refuses. */}
            {classId && !loadingResults && needsHumanReason && (
              <Alert severity="info" sx={{ mb: 2 }} data-testid="placings-needs-human">
                {t(NEEDS_HUMAN_TEXT_KEYS[needsHumanReason])}
              </Alert>
            )}

            {/* #3278 -- the class's placings changed underneath the open form
                (auto-place, or another admin). Plain notice, and an explicit
                line when unsaved edits were replaced rather than dropped
                silently. */}
            {classId && !loadingResults && changedElsewhere && (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="placings-changed-elsewhere">
                {t(
                  changedElsewhere.kind === "placed"
                    ? "placings.changedElsewherePlaced"
                    : changedElsewhere.kind === "unplaced"
                      ? "placings.changedElsewhereUnplaced"
                      : "placings.changedElsewhereChanged"
                )}
                {changedElsewhere.hadUnsaved && (
                  <Box data-testid="placings-changed-elsewhere-unsaved">
                    {t("placings.changedElsewhereUnsaved")}
                  </Box>
                )}
              </Alert>
            )}

            {/* #2862 — the placed-class lock banner. Read-only for everyone
                once placings are set; the explicit "Unlock to edit" override
                action renders ONLY for a score:override_placed_class holder
                (score_admin) — a non-admin gets the lock with no affordance
                (AC1), an admin gets the explicit override (AC2). */}
            {classId && !loadingResults && classIsPlaced && (
              <Alert
                severity={lockedRideIds.size > 0 ? "info" : "warning"}
                sx={{ mb: 2 }}
                data-testid="placings-lock-banner"
                action={
                  canOverridePlacedClass && lockedRideIds.size > 0 ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setOverrideUnlocked(true)}
                      data-testid="placings-override-unlock"
                    >
                      {t("placings.unlockToEdit")}
                    </Button>
                  ) : undefined
                }
              >
                {lockedRideIds.size === 0
                  ? t("placings.overrideActive")
                  : groupByDivision && !placingsLocked
                    ? t(
                        canOverridePlacedClass
                          ? "placings.lockedDivisionsAdmin"
                          : "placings.lockedDivisions",
                        { divisions: lockedDivisionLabels.join(", ") }
                      )
                    : canOverridePlacedClass
                      ? t("placings.lockedAdmin")
                      : t("placings.lockedReadOnly")}
              </Alert>
            )}

            {/* #3464: which divisions of a placed-by-division class still need
                placings. Names them so the office knows where to go next. */}
            {classId && !loadingResults && unplacedDivisionLabels.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="placings-unplaced-divisions">
                {t("placings.unplacedDivisions", { divisions: unplacedDivisionLabels.join(", ") })}
              </Alert>
            )}

            {/* #3465: out-of-date placings. Same Needs placings badge; this names the
                rides that would move. Re-placing follows the placed-class lock. */}
            {classId && !loadingResults && outOfDateRideMoves.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="placings-out-of-date">
                <AlertTitle>{t("placings.outOfDate.title")}</AlertTitle>
                <Box sx={{ mb: 1 }}>{t("placings.outOfDate.body")}</Box>
                <Table size="small" data-testid="placings-out-of-date-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("placings.outOfDate.ride")}</TableCell>
                      <TableCell>{t("placings.outOfDate.oldPlace")}</TableCell>
                      <TableCell>{t("placings.outOfDate.newPlace")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outOfDateRideMoves.map((move) => (
                      <TableRow key={move.rideId} data-testid={`placings-out-of-date-row-${move.rideId}`}>
                        <TableCell>
                          <RideLink ride={ridesById[move.rideId]} rideId={move.rideId} fallback={move.rideId} />
                        </TableCell>
                        <TableCell data-testid={`placings-out-of-date-old-${move.rideId}`}>
                          {move.oldPlace ? move.oldPlace : t("placings.outOfDate.noPlace")}
                        </TableCell>
                        <TableCell data-testid={`placings-out-of-date-new-${move.rideId}`}>
                          {move.newPlace ? move.newPlace : t("placings.outOfDate.noPlace")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Alert>
            )}

            {/* #2159 — advisory short-panel notice. Outlined "warning" (amber),
                never "error": nothing here blocks Place/confirm/publish. */}
            {classId && !loadingResults && judgeFloorWarning && (
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ mb: 2 }}
                data-testid="placings-judge-floor-warning"
              >
                <AlertTitle>{t("placings.judgeFloorTitle")}</AlertTitle>
                <Box data-testid="placings-judge-floor-message">{judgeFloorWarning.message}</Box>
                <Box sx={{ mt: 0.5 }}>{t("placings.judgeFloorAdvisory")}</Box>
              </Alert>
            )}

            {/* #3368 -- the class cannot finalize: judge count outside the test's
                allowed panel size. Error severity (it IS blocking), with the fix. */}
            {classId && !loadingResults && panelSizeViolation && (
              <Alert
                severity="error"
                variant="outlined"
                sx={{ mb: 2 }}
                data-testid="placings-panel-size-error"
              >
                <AlertTitle>{t("placings.panelSizeTitle")}</AlertTitle>
                <Box data-testid="placings-panel-size-message">
                  {panelSizeViolation.direction === "over"
                    ? t("placings.panelSizeOver", {
                        count: panelSizeViolation.expectedJudgeCount,
                        max: panelSizeViolation.maxJudges,
                      })
                    : t("placings.panelSizeUnder", {
                        count: panelSizeViolation.expectedJudgeCount,
                        min: panelSizeViolation.minJudges,
                      })}
                </Box>
                <Box sx={{ mt: 0.5 }}>{t("placings.panelSizeFix")}</Box>
              </Alert>
            )}

            {/* #2163 -- advisory DR123.1 unsigned-but-tabulated notice. Outlined
                "warning", never "error": nothing here blocks Place/confirm/publish. */}
            {classId && !loadingResults && signatureWarning && (
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ mb: 2 }}
                data-testid="placings-signature-warning"
              >
                <AlertTitle>{t("placings.signatureWarningTitle")}</AlertTitle>
                <Box data-testid="placings-signature-message">{signatureWarning.message}</Box>
                <Box sx={{ mt: 0.5 }}>{t("placings.signatureWarningAdvisory")}</Box>
              </Alert>
            )}

            {placeBanner && (
              <Alert severity={placeBanner.severity} sx={{ mb: 2 }} data-testid="placings-banner">
                {placeBanner.message}
                {placeBanner.blockingRideIds?.length > 0 && (
                  <Box sx={{ mt: 1 }} data-testid="placings-blocked-unscored-hint">
                    {t("placings.blockedUnscoredHint")}
                  </Box>
                )}
                {placeBanner.blockingRideIds?.length > 0 && (
                  <Box component="ul" sx={{ m: "8px 0 0", pl: 2 }}>
                    {/* #902 — blockingRides (rich {rideId, wasEverConfirmed} shape) drives
                        the reopened-vs-never-scored distinction; blockingRideIds alone
                        (older/back-compat 409 shape) falls back to "never scored" styling. */}
                    {(
                      placeBanner.blockingRides ||
                      placeBanner.blockingRideIds.map((rideId) => ({ rideId, wasEverConfirmed: false }))
                    ).map(({ rideId, wasEverConfirmed }) => (
                      <li key={rideId} data-testid={`placings-blocking-ride-${rideId}`}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
                        >
                          <RideLink ride={ridesById[rideId]} rideId={rideId} fallback={rideId} />
                          <Chip
                            size="small"
                            color={wasEverConfirmed ? "warning" : "default"}
                            variant={wasEverConfirmed ? "filled" : "outlined"}
                            label={
                              wasEverConfirmed
                                ? t("placings.blockingReopened")
                                : t("placings.blockingNeverScored")
                            }
                            data-testid={`placings-blocking-status-${rideId}`}
                          />
                          {/* #2169 -- the only inline resolution the banner previously
                              offered a reopened-confirmed ride was Scratch, the wrong
                              Term Reason (the horse rode; it was not scratched). Rather
                              than surface the real reopen/re-confirm choice on THIS
                              screen (rejected -- that is a scoring decision and belongs
                              in the ride editor, per Jaybill's 2026-09-01 decision on
                              the card), this button only navigates straight to the
                              blocking ride in RideEditor (App.jsx `/rides/:rideId`,
                              same route RideLink above already uses) so the admin
                              reaches the correct path in one click instead of a hunt.
                              Plain react-router Link rendered as a Button: no onClick,
                              no scoreApi call, so it cannot reopen, confirm, terminate,
                              or otherwise mutate the ride -- it can only navigate. */}
                          <Button
                            size="small"
                            variant="outlined"
                            // #902's dark-mode-invisible-outlined-button trap
                            // applies to any unset outlined color, not just
                            // the one it was first found on -- pin "info" so
                            // this reads in both themes.
                            color="info"
                            component={RouterLink}
                            to={`/rides/${rideId}`}
                            data-testid={`placings-open-ride-${rideId}`}
                          >
                            {t("placings.openRide")}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={resolvingRideId === rideId}
                            onClick={() => handleResolveBlockingRide(rideId, "scratch")}
                            data-testid={`placings-scratch-${rideId}`}
                          >
                            {resolvingRideId === rideId ? t("placings.resolving") : t("placings.scratch")}
                          </Button>
                          {/* Card #1930 AC7 — the honest third exit. Without this the only
                              ways past a ridden-but-blank ride were Scratch (wrong: she
                              rode) or inventing marks (the WEC 2026-08-21 incident).
                              Only offered for a ride that was never confirmed: a ride
                              that was scored and reopened holds marks, which score-api
                              refuses to hide behind "no score". Opens a reason dialog;
                              the reason is required and goes to the ride's audit trail. */}
                          {!wasEverConfirmed && (
                            <Button
                              size="small"
                              color="info"
                              disabled={resolvingRideId === rideId}
                              onClick={() => {
                                setNoScoreError(null);
                                setNoScoreRideId(rideId);
                              }}
                              data-testid={`placings-no-score-${rideId}`}
                            >
                              {t("placings.recordNoScore")}
                            </Button>
                          )}
                        </Stack>
                      </li>
                    ))}
                  </Box>
                )}
                {/* #2057 -- names every blocked row AND its specific
                    DR123.3 outcome (AC2), matching this same banner's
                    blockingRideIds treatment above. No "place anyway" here:
                    unlike the duplicate-placing warning below, a term reason
                    is a hard block with no override (Jaybill's 2026-08-30
                    decision) -- the only recovery is blanking the row's
                    placing, then pressing Place again. */}
                {placeBanner.termBlockedEntries?.length > 0 && (
                  <Box component="ul" sx={{ m: "8px 0 0", pl: 2 }} data-testid="placings-term-blocked-warning">
                    {placeBanner.termBlockedEntries.map(({ rideId, termReason }) => (
                      <li key={rideId} data-testid={`placings-term-blocked-${rideId}`}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "wrap", rowGap: 0.5 }}
                        >
                          <RideLink ride={ridesById[rideId]} rideId={rideId} fallback={rideId} />
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={termReason}
                            data-testid={`placings-term-blocked-reason-${rideId}`}
                          />
                        </Stack>
                      </li>
                    ))}
                  </Box>
                )}
                {placeBanner.overPlacesAwarded?.length > 0 && (
                  <Box sx={{ mt: 1 }} data-testid="placings-over-places-awarded-warning">
                    <Box component="ul" sx={{ m: "8px 0", pl: 2 }}>
                      {placeBanner.overPlacesAwarded.map(({ rideId, value }) => (
                        <li key={rideId} data-testid={`placings-over-places-awarded-${rideId}`}>
                          {t("placings.overPlacesAwardedRide", { value })}{" "}
                          <RideLink ride={ridesById[rideId]} rideId={rideId} fallback={rideId} />
                        </li>
                      ))}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      disabled={placeSubmitting}
                      onClick={() => handlePlace({ skipOverPlacesCheck: true })}
                      data-testid="placings-over-places-awarded-place-anyway"
                    >
                      {t("placings.placeAnyway")}
                    </Button>
                  </Box>
                )}
                {placeBanner.duplicateGroups?.length > 0 && (
                  <Box sx={{ mt: 1 }} data-testid="placings-duplicate-warning">
                    <Box component="ul" sx={{ m: "8px 0", pl: 2 }}>
                      {placeBanner.duplicateGroups.map(({ value, rideIds }) => (
                        <li key={value} data-testid={`placings-duplicate-group-${value}`}>
                          {t("placings.duplicatePlacingGroup", { value })}
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.5 }}
                          >
                            {rideIds.map((rideId) => (
                              <RideLink
                                key={rideId}
                                ride={ridesById[rideId]}
                                rideId={rideId}
                                fallback={rideId}
                              />
                            ))}
                          </Stack>
                        </li>
                      ))}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      // #904 — same dark-mode-legibility fix #902 applied to
                      // the banner's resolve button: MUI's unset outlined color
                      // is near-invisible in this brand's dark theme.
                      color="warning"
                      disabled={placeSubmitting}
                      onClick={() => handlePlace({ skipDuplicateCheck: true, skipOverPlacesCheck: true })}
                      data-testid="placings-place-anyway"
                    >
                      {t("placings.placeAnyway")}
                    </Button>
                  </Box>
                )}
              </Alert>
            )}
            <NoScoreRecordedDialog
              open={noScoreRideId != null}
              rideLabel={ridesById[noScoreRideId]?.competitorNumber ?? ridesById[noScoreRideId]?.riderName ?? ""}
              saving={resolvingRideId != null && resolvingRideId === noScoreRideId}
              error={noScoreError}
              onClose={() => setNoScoreRideId(null)}
              onConfirm={(reason) => handleResolveBlockingRide(noScoreRideId, "noScore", reason)}
            />
            {resolveError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setResolveError(null)}
                data-testid="placings-resolve-error"
              >
                {resolveError?.response?.data?.data?.message || t("placings.resolveError")}
              </Alert>
            )}

            {classId && loadingResults && <TableSkeleton rows={4} columns={6} />}
            {classId && !loadingResults && results.length === 0 && (
              <Typography color="text.secondary">{t("placings.noResults")}</Typography>
            )}
            {classId && !loadingResults && results.length > 0 && (
              <>
                <TableContainer data-testid="placings-table">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {headerColumns.map((col) => (
                          <TableCell key={col.key} data-testid={col.testid}>
                            {col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* #945 — displayGroups is `results` grouped/sorted per
                          the active view (buildPlacingGroups); a group with a
                          non-null label renders its own separator row ahead
                          of its rides (score view's single "all" group has
                          label:null so only the trailing term'd separator, if
                          any, shows — division view gets one per division). */}
                      {displayGroups.flatMap((group) => {
                        // #946 — ties are only meaningful within the same
                        // rendered group (e.g. not across two divisions in
                        // the division view, which place separately).
                        const tieIndicators = tieIndicatorsFor(group.rows);
                        const rows = group.rows.map((row) => (
                          <TableRow key={row.rideId} data-testid={`placing-row-${row.rideId}`}>
                            <TableCell sx={{ width: 88 }}>
                              {/* #3365 -- hors de concours (USEF DR119.5, docs/rules/
                                  usef/rulebook-dr-dressage-2026.pdf): the ride is
                                  scored but takes no place, so there is no
                                  suggestion and no Placing box at all, only an "HC"
                                  chip that says why (a disabled box looked the same
                                  as an enabled one). Nothing is ever sent as a
                                  place for it. */}
                              {row.hc === true ? (
                                <Chip
                                  size="small"
                                  label={t("placings.hc")}
                                  title={t("placings.hcHint")}
                                  data-testid={`placing-hc-${row.rideId}`}
                                />
                              ) : (
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{
                                  min: 0,
                                  step: 1,
                                  "aria-label": t("placings.columns.placing"),
                                  "data-testid": `placing-input-${row.rideId}`,
                                }}
                                // #904 — a duplicate placing is a valid number
                                // (so it doesn't trip the format `error` prop
                                // above), but still gets its own warning-colored
                                // outline so the collision is visible right on
                                // the row, not just in the banner.
                                sx={{
                                  // #3340 — 3 characters plus a little
                                  // padding each side (the 64px root less
                                  // 8px padding a side leaves ~48px, room
                                  // for "999" at the 16px input font), down
                                  // from 88px; the cell above shrinks with
                                  // it. The value is centered in the input.
                                  width: 64,
                                  // #2855 — the client's screenshot ask: the
                                  // native type="number" spinner is oversized
                                  // in this compact cell. Removed (not just
                                  // shrunk) across the engines that render
                                  // one: Blink/WebKit via the two
                                  // ::-webkit-*-spin-button pseudo-elements,
                                  // Firefox via -moz-appearance on the input.
                                  // Keyboard/click entry is untouched; #2850's
                                  // blur-on-wheel guard below still stands on
                                  // its own (it fires on any wheel over the
                                  // box, spinner or not).
                                  "& input::-webkit-outer-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "& input::-webkit-inner-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "& input": {
                                    MozAppearance: "textfield",
                                    textAlign: "center",
                                    paddingLeft: "8px",
                                    paddingRight: "8px",
                                  },
                                  ...(duplicatePlacingRideIds.has(row.rideId) &&
                                  !invalidRideIds.has(row.rideId) &&
                                  !termBlockedRideIds.has(row.rideId)
                                    ? {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "warning.main",
                                          borderWidth: 2,
                                        },
                                      }
                                    : {}),
                                }}
                                value={editValues[row.rideId] ?? ""}
                                disabled={placeSubmitting || lockedRideIds.has(row.rideId)}
                                // #2850 — Chromium steps a FOCUSED
                                // type="number" input's value on any trusted
                                // wheel event over it, so an operator
                                // scrolling the page with the cursor parked
                                // on a placing box silently rewrote the
                                // entered placing. Blur on wheel hands the
                                // gesture back to the page (which then
                                // scrolls normally) before the browser's
                                // step-the-focused-input default can apply;
                                // keyboard/click entry is untouched. Applies
                                // to every row, since this is the one
                                // per-row TextField the table renders.
                                onWheel={(e) => e.target.blur()}
                                // #2057 -- a term-blocked row's placing value is
                                // a real, valid integer (it wouldn't trip the
                                // format check invalidRideIds guards), but it
                                // still can't be placed, so it gets the same
                                // red `error` styling as a format error --
                                // visible right on the row, not only after a
                                // Place attempt surfaces the banner above.
                                error={invalidRideIds.has(row.rideId) || termBlockedRideIds.has(row.rideId)}
                                onChange={(e) =>
                                  setEditValues((prev) => ({
                                    ...prev,
                                    [row.rideId]: e.target.value,
                                  }))
                                }
                              />
                              )}
                            </TableCell>
                            {/* Card #3382 -- the old single "#CNO, Rider on Horse"
                                cell (#860) is split into CNO / rider first /
                                rider last / horse columns. The ride link
                                (RideLink -> RideEditor route, #860) stays on
                                the CNO cell only, so a row still has exactly one
                                anchor. First/last come from the real Person
                                fields score-api's enrichRideNames carries
                                (riderFirstName/riderLastName), never a split
                                of riderName (multi-word surnames). A ride with
                                neither (manual ride, free-text name) shows its
                                riderName whole in the last-name column.
                                #2858 -- the link stays out of the TAB sequence
                                (tabIndex -1): it sits between two rows'
                                placing inputs in DOM order, so leaving it
                                tabbable made Tab hop ACROSS the row instead of
                                DOWN to the next placing field. Still rendered
                                and mouse-clickable; the keyboard user reaches
                                the same ride editor via the banner's
                                Open-ride button or the class detail route. */}
                            <TableCell data-testid={`placing-cno-${row.rideId}`}>
                              <RideLink
                                ride={{ competitorNumber: ridesById[row.rideId]?.competitorNumber }}
                                rideId={row.rideId}
                                fallback={t("placings.unknownRide")}
                                tabIndex={-1}
                              />
                            </TableCell>
                            <TableCell data-testid={`placing-rider-first-${row.rideId}`}>
                              {ridesById[row.rideId]?.riderFirstName || ""}
                            </TableCell>
                            <TableCell data-testid={`placing-rider-last-${row.rideId}`}>
                              {ridesById[row.rideId]?.riderLastName ||
                                (ridesById[row.rideId]?.riderFirstName ? "" : ridesById[row.rideId]?.riderName) ||
                                ""}
                            </TableCell>
                            <TableCell data-testid={`placing-horse-${row.rideId}`}>
                              {ridesById[row.rideId]?.horseName || ""}
                            </TableCell>
                            <TableCell data-testid={`placing-test-${row.rideId}`}>
                              {/* #915 — resolved per-ride off ride.testId against the
                                  bundled catalog (never per-class): a Test-of-Choice
                                  class can carry a different test per ride. */}
                              {getRideTestName(ridesById[row.rideId], catalogDefsById) || "—"}
                            </TableCell>
                            <TableCell>
                              {/* Card #2840 -- a term'd ride never reports a
                                  score value, so this never falls back to
                                  score-api's own real (non-null) composite
                                  for a sheet-level elimination (calculation.js
                                  zeroes it, it isn't withheld -- see
                                  score-suppression.js's header comment): the
                                  shared suppressScoreValue gate replaces
                                  formatPercentage's already-formatted string
                                  with the same em dash glyph every other
                                  surface uses, keyed off the SAME
                                  termReasonsById resolveDisplayTermReason
                                  already populated above. */}
                              {suppressScoreValue(termReasonsById[row.rideId], formatPercentage(row.composite))}
                              <TieIndicator
                                value={tieIndicators.get(row.rideId)}
                                rideId={row.rideId}
                                t={t}
                              />
                            </TableCell>
                            {/* #1999 (AC5): the raw points sum DR123.3/
                                DR129.5 actually use to separate this
                                percentage tie, not a percentage itself.
                                Display only: never read by handlePlace,
                                compareByComposite, or tieIndicatorsFor.
                                Card #2840 -- suppressed the same way the
                                Composite cell above is: a tie-break value is
                                still a subtotal a term'd ride must never
                                report. */}
                            {tieBreakField && (
                              <TableCell data-testid={`placing-tiebreak-${row.rideId}`}>
                                {suppressScoreValue(termReasonsById[row.rideId], formatTieBreakValue(row[tieBreakField]))}
                              </TableCell>
                            )}
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {/* #2800 -- ONE badge naming the real outcome. The placing's own
                                    resolved term reason (termReasonsById, the 4-tier
                                    deriveRideTermReason read in applyResults) wins;
                                    the batch endpoint's termReason is the fallback
                                    for a ride this table has not resolved. The old
                                    second `placing-term-reason-*` chip (#2063) is
                                    folded in here, same testid, so it no longer
                                    sits beside a badge that says Final. */}
                                <RideStatusBadge
                                  status={rideStatuses[row.rideId]}
                                  termReason={termReasonsById[row.rideId] ?? rideTermReasons[row.rideId]}
                                  data-testid={
                                    termReasonsById[row.rideId]
                                      ? `placing-term-reason-${row.rideId}`
                                      : undefined
                                  }
                                />
                                {/* #1886 AC2 — a committed placing is exactly
                                    what this page's Place button promises to
                                    "release to FVD" (see handlePlace above),
                                    so once `row.placing` is set, whether FVD
                                    actually confirmed it belongs right next
                                    to the status that reads Final, not only
                                    on the separate ClassManager list (#554).
                                    Absent fvdPushStatus reads NOT_POSTED
                                    (gray) here exactly like it does there —
                                    never silently omitted just because this
                                    is the Place screen. */}
                                {/* #1785 — a stored placing of 0 means
                                    UNPLACED, same as null; excluded here too
                                    so an actually-unplaced row never shows a
                                    "not released" badge for a release that
                                    was never attempted. */}
                                {/* #1886 — `row.writeBackBlocked` (results.js,
                                    stamped from the show's own `noFoxbox`
                                    admin flag since #1015) means this show
                                    was deliberately told never to push to
                                    FVD at all: foxbox's own IsReleased gate
                                    excludes these rides by design (see
                                    show-config.js's #1015 comment), so
                                    "release never happened" isn't a failure
                                    here, it's the configured behavior an
                                    admin already chose in Settings. Suppress
                                    the icon rather than invent a fourth
                                    state: a permanent gray/red badge on
                                    every placed row of a demo/test show
                                    would be constant noise about a release
                                    that was never going to occur, not signal
                                    — the same "doesn't apply, don't show it"
                                    call #1785 already made for placing 0. */}
                                {row.placing != null &&
                                  row.placing !== 0 &&
                                  !row.writeBackBlocked && (
                                  <FvdPushStatusIcon
                                    fvdPushState={row.fvdPushStatus ?? "NOT_POSTED"}
                                    error={row.fvdPushError}
                                    size={16}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            {!groupByDivision && (
                              <TableCell>
                                {/* #821 — rider's division. FVD has no literal division
                                    field; the genuine per-rider value is
                                    Ride.riderStatus (FVD tblRider.chrStatus, joined
                                    server-side in score-api's enrichRideNames — see
                                    #784). Blank for manual/unresolved rides, shown
                                    as a dash rather than invented. */}
                                <Typography variant="body2" color="text.secondary">
                                  {ridesById[row.rideId]?.riderStatus || "—"}
                                </Typography>
                              </TableCell>
                            )}
                          </TableRow>
                        ));
                        if (group.label == null) return rows;
                        return [
                          <TableRow key={`group-${group.key}`} data-testid={`placings-group-${group.key}`}>
                            <TableCell
                              colSpan={headerColumns.length}
                              sx={{ bgcolor: "action.hover", fontWeight: 600 }}
                            >
                              {group.label}
                            </TableCell>
                          </TableRow>,
                          ...rows,
                        ];
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handlePlace}
                    disabled={placeSubmitting || placingsLocked}
                    id={PLACE_BUTTON_ANCHOR_ID}
                    data-testid="placings-place-button"
                  >
                    {placeSubmitting ? t("placings.placingInProgress") : t("placings.place")}
                  </Button>
                  {!classIsPlaced && (
                    <Button
                      variant="outlined"
                      onClick={() => setPlaceByRulesOpen(true)}
                      disabled={placeSubmitting}
                      data-testid="placings-place-by-rules-button"
                    >
                      {t("placeByRules.placeByRules")}
                    </Button>
                  )}
                  {placeSubmitting && <CircularProgress size={20} />}
                </Stack>
                {placeByRulesOpen && (
                  <PlaceByRulesDialog
                    classId={classId}
                    classNumber={selectedClass?.classNumber}
                    showId={selectedClass?.showId}
                    hideOpenPlacings
                    onClose={() => setPlaceByRulesOpen(false)}
                    onPlaced={handlePlacedByRules}
                  />
                )}
              </>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
